import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ColDef } from "ag-grid-community";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import checkMarkIcon from "../assets/checkMark.svg";
import CIS_image from "../assets/CIS_image.svg";
import NIST_image from "../assets/nist.svg";
import arista_dark from "../assets/arista-white.svg";
import unCheckMarkIcon from "../assets/unCheckMark.svg";
import axiosInstance from "../axios/axios";
import AgGridTable from "../components/table/AgGridTable";
import FrameworkNotEnabled from "../components/FrameworkNotEnabled";
import { END_POINTS } from "../shared";
import useFrameworks, {
  FRAMEWORK_NOT_ENABLED_MESSAGE,
  isFrameworkNotEnabledError,
} from "../shared/hooks/useFrameworks";
import { setComplianceTableRowId } from "./CISCompliance.slice";
import { setComplianceWidget } from "./onPremises/ComplianceWidget.slice";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import cis_light from "../assets/cis_light.svg";
import nistLogo from "../assets/nist.svg";
import nist_dark from "../assets/nist_dark.svg";
import spark_dark from "../assets/spark_dark.svg";
import cis_dark from "../assets/cis_dark.svg";
import sparkLogo from "../assets/Spark-text.svg";
import PCI_DSS_logo from "../assets/PCI_DSS_logo.svg";
import controlRequirementMapping from "./control_requirement_mapping.json";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CallReceivedIcon from "@mui/icons-material/CallReceived";

// Empty trend data objects for type safety
const MOCK_TREND_DATA = {
  series: [],
  categories: [],
};

const NIST_TREND_DATA = {
  series: [],
  categories: [],
  percentChange: "0%",
  isPositive: true,
};

const PCI_DSS_TREND_DATA = {
  series: [],
  categories: [],
  percentChange: "0%",
  isPositive: true,
};

// --- Interfaces copied from ComplianceDashboard ---
type ControlVersion = {
  id: string;
  version: string;
};

type FilterOption = {
  id: string;
  name: string;
};

type ControlType = {
  id: string;
  name: string;
  versions: ControlVersion[];
  filter: FilterOption[] | null;
};

interface GraphDataPoint {
  date_time: string;
  new_event: boolean;
  device_count: number;
  "Implementation Group 1": number;
  "Implementation Group 2": number;
  "Implementation Group 3": number;
}

interface ComplianceSummary {
  id: string;
  control_type: string;
  compliance: number;
  stage: string;
  type: string[];
  passed_count: number;
  failed_count: number;
  total_count: number;
  filter: string;
}

// --- ComplianceWidget component copied from ComplianceDashboard ---
interface ComplianceWidgetProps {
  data: ComplianceSummary;
  controlTypes?: ControlType[]; // Make optional for details page
  setComplianceData?: React.Dispatch<React.SetStateAction<ComplianceSummary[]>>; // Make optional for details page
  onWidgetClick?: (data: ComplianceSummary) => void;
}

const ComplianceWidget = ({
  data,
  controlTypes,
  setComplianceData,
  onWidgetClick,
}: ComplianceWidgetProps) => {
  // Removed state and logic related to dropdowns/filters as they might not be needed here
  // or will be handled differently for the details page.
  const [compliancePercentage, setCompliancePercentage] = useState(
    data.compliance
  );
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  console.log(data, "widget");
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate(); // Use navigate hook
  const getColorByFilter = (filter: string) => {
    switch (filter?.toLowerCase()) {
      case "ig1":
        return {
          color: "#6CBD54",
          type: "gradient",
          gradient: {
            shade: "dark",
            type: "horizontal",
            shadeIntensity: 0.5,
            gradientToColors: ["#325727"],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        };
      case "ig2":
        return {
          color: "#F08B0E",
          type: "gradient",
          gradient: {
            shade: "dark",
            type: "horizontal",
            shadeIntensity: 0.5,
            gradientToColors: ["#8A5008"],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        };
      case "ig3":
        return {
          color: "#015157",
          type: "gradient",
          gradient: {
            shade: "dark",
            type: "horizontal",
            shadeIntensity: 0.5,
            gradientToColors: ["#02AFBD"],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        };
      default:
        return { color: "#e0e0e0", type: "solid" };
    }
  };

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  // Update compliance percentage when data changes
  useEffect(() => {
    setLoading(true);
    setCompliancePercentage(data.compliance);
  }, [trigger, data.compliance]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        height: 170,
        type: "radialBar" as const,
        toolbar: {
          show: false,
        },
        background: "transparent",
      },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 225,
          hollow: {
            margin: 0,
            size: "70%",
            background:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front" as const,
            dropShadow: {
              enabled: true,
              top: 3,
              left: 0,
              blur: 4,
              opacity: 0.5,
            },
          },
          track: {
            background:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "#f0f0f0",
            strokeWidth: "100%", // <-- Make the track width 100% to match the filled width
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -3,
              left: 0,
              blur: 4,
              opacity: 0.35,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: -10,
              show: true,
              color: theme.palette.text.secondary,
              fontSize: "17px",
              formatter: function () {
                return (
                  data.filter ||
                  data.control_type ||
                  data.framework ||
                  "COMPLIANCE"
                ).toUpperCase();
              },
            },
            value: {
              formatter: function (val: number) {
                return parseInt(val.toString(), 10).toString() + "%";
              },
              color: theme.palette.text.primary,
              fontSize: "22px",
              fontWeight: 700,
              show: true,
              offsetY: 8,
            },
          },
        },
      },
      fill: (() => {
        const colorObj = getColorByFilter(data.filter);
        if (colorObj.type === "gradient" && colorObj.gradient) {
          return {
            type: "gradient",
            gradient: colorObj.gradient,
            colors: [colorObj.color],
          };
        }
        return {
          type: "solid",
          colors: [colorObj.color],
        };
      })(),
      stroke: {
        lineCap: "round" as const,
      },
      labels: ["Compliance"],
      series: [compliancePercentage],
      tooltip: {
        enabled: true,
        style: { fontSize: "12px" },
        custom: function ({ series, seriesIndex, w }: any) {
          const value = Math.round(series[seriesIndex]);
          return (
            '<div style="padding: 8px; background: white; color: black; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">' +
            '<span style="font-weight: 500;">Compliance : ' +
            value +
            "% </span>" +
            "</div>"
          );
        },
      },
    }),
    [compliancePercentage, theme, data.filter]
  );

  const getControlImage = (controlType: string) => {
    if (controlType.toUpperCase().includes("CIS")) {
      return CIS_image;
    } else if (controlType.toUpperCase().includes("NIST")) {
      return NIST_image;
    } else if (controlType.toUpperCase().includes("PCI")) {
      return PCI_DSS_logo;
    } else if (controlType.toUpperCase().includes("SPARK")) {
      return isDarkMode ? spark_dark : sparkLogo;
    }
    return null; // Or a default image
  };

  // Simplified click handler - we are already on the details page
  const handleCardClick = () => {
    if (onWidgetClick) {
      onWidgetClick(data);
    }
  };

  return (
    <Box
      sx={{
        padding: 0,
        textAlign: "center",
        position: "relative",
        borderRadius: "12px",
        transition: "transform 0.2s",
        "&:hover": {
          transform: "scale(1.03)",
        },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      onClick={handleCardClick}
    >
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          gap: 0.5,
          // top: 8,
          // right: 8
        }}
      ></Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
          {data.stage}
        </Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ReactApexChart
          options={chartOptions}
          series={chartOptions.series}
          type="radialBar"
          height={170}
        />

      </Box>
      {/* CIS */}
      <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
        <span
          style={{
            color: "#43a047",
            fontWeight: 700,
            fontSize: 18,
            marginRight: 6,
          }}
        >
          ✔
        </span>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 16,
            mr: 1,
          }}
        >
          Passed :
        </Typography>
        <Typography sx={{ fontWeight: 700, color: "#43a047", fontSize: 16 }}>
          {data.passed_count}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center">
        <span
          style={{
            color: "#e53935",
            fontWeight: 700,
            fontSize: 18,
            marginRight: 6,
          }}
        >
          ✖
        </span>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 16,
            mr: 1,
          }}
        >
          Failed :
        </Typography>
        <Typography sx={{ fontWeight: 700, color: "#e53935", fontSize: 16 }}>
          {data.failed_count}
        </Typography>
      </Box>
    </Box>
  );
};

const NistDetailsDashboard: React.FC<{ version?: string }> = ({ version }) => {
  const theme = useTheme();
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [trendSeries, setTrendSeries] = useState<any[]>([]);
  const [trendCategories, setTrendCategories] = useState<any[]>([]);
  // Set when the backend refuses this framework (switched off for the organization)
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);
  const TableData = useSelector((state: any) => state.ComplianceWidget);
  console.log("TableData", TableData);

  useEffect(() => {
    // Scroll to the top of the page when the component is mounted
    window.scrollTo(0, 0);
  }, []);

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setIsLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    const fetchTrendData = async () => {
      setIsLoading(true);
      try {
        const today = dayjs().format("YYYY-MM-DD");
        const sevenDaysAgo = dayjs().subtract(7, "day").format("YYYY-MM-DD");
        const payload = {
          device_id: TableData?.Device_id || TableData?.device_id,
          duration: "custom_date_range",
          start_date: sevenDaysAgo,
          end_date: today,
          framework_data: version ? version.replace(/\s+/, "_") : "", // replace spaces with underscores, handle undefined
          is_compliance: true,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        const response = await axiosInstance.post(
          END_POINTS.DASHBOARD_COMPLIANCE,
          payload
        );
        if (response.status === 200 && response.data?.data?.graph_data) {
          const graphData = response.data.data.graph_data as any[];
          setTrendCategories(graphData.map((item: any) => item.date_time));

          const excludedKeys = ["date_time", "device_count", "new_event", "date", "time"];
          const keysSet = new Set<string>();
          graphData.forEach((item: any) => {
            Object.keys(item).forEach((key) => {
              if (!excludedKeys.includes(key)) {
                keysSet.add(key);
              }
            });
          });

          const keys = Array.from(keysSet);
          if (keys.length > 0) {
            const series = keys.map((key) => ({
              name: version || key,
              data: graphData.map((item: any) => item[key] ?? 0),
            }));
            setTrendSeries(series);

            const seriesData = series[0].data;
            const firstValue = seriesData.length > 0 ? seriesData[0] : 0;
            const lastValue = seriesData.length > 0 ? seriesData[seriesData.length - 1] : 0;
            const diff = lastValue - firstValue;
            NIST_TREND_DATA.percentChange = `${Math.abs(diff).toFixed(1)}%`;
            NIST_TREND_DATA.isPositive = diff >= 0;
          } else {
            setTrendSeries([]);
          }
        }
      } catch (error) {
        console.error("Error fetching compliance trend data:", error);
        if (isFrameworkNotEnabledError(error)) setFrameworkDisabled(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (version) {
      fetchTrendData();
    }
  }, [trigger, version, TableData?.Device_id, TableData?.device_id]);

  const handleNistRowClick = (params: any) => {
    setSelectedRow(params);
    setDrawerOpen(true);
  };

  const [complianceData, setComplianceData] = useState<ComplianceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);

  const TableData_isWidgetClicked = complianceData?.map(
    (item: any, index: number) => ({
      number: index + 1,
      id: item.id,
      controlId: item.control_id,
      name: item.name,
      description: item.description,
      compliance: item.percentage,
      recommendations: item.controls_count,
      stage: item.stage,
    })
  );
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      // "& .ag-body-vertical-scroll": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      // "& .ag-body-vertical-scroll-viewport": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  const TableData_WidgetClicked__isTABLE_COL_DEFS = [
    {
      headerName: "#",
      field: "number",
      minWidth: 80,
      maxWidth: 100,
      lockPosition: true,
      lockPinned: true,
      filter: false,
      width: 90,
    },
    {
      headerName: "Control ID",
      field: "controlId",
      filter: true,
      sortable: true,
      minWidth: 120,
      maxWidth: 150,
      width: 150,

      cellStyle: {
        display: "flex",
        // justifyContent: "center",
        alignItems: "center",
      },
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip title={value}>
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "bold",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
    },

    {
      headerName: "Name",
      field: "name",
      filter: true,
      sortable: true,
      flex: 2,
      cellStyle: {
        display: "flex",
        // justifyContent: "center",
        alignItems: "center",
      },
      minWidth: 500,
      width: 350,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip title={value}>
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "bold",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
    },
    {
      headerName: "Description",
      field: "description",
      flex: 2,
      minWidth: 300,

      width: 400,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip
            title={
              <div
                className="scroll"
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  whiteSpace: "pre-line",
                  paddingRight: 8,
                }}
              >
                {value}
              </div>
            }
          >
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "600",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
      cellStyle: {
        display: "flex",
        // justifyContent: "center",
        alignItems: "center",
      },
    },

    {
      headerName: "Compliance %",
      field: "compliance",
      sortable: true,
      minWidth: 150,
      maxWidth: 200,
      width: 180,

      cellRenderer: (params: any) => {
        const value = Number(params.value) || 0;

        // Choose progress color based on value
        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row", // explicitly set row direction
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: 40,
                height: 40,
              }}
            >
              {/* Background Circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{
                  color: "#c5c5d2", // background circle
                }}
              />
              {/* Foreground Progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
              {`${value}%`}
            </Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      headerName: "Recommendations",
      field: "recommendations",

      minWidth: 200,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <span style={{ width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
                width: "100%",
                fontWeight: "500",
              }}
            >
              {value}
            </Typography>
          </span>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];



  useEffect(() => {
    setIsTableLoading(true);
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get(
          `/securityscan/cd-compliance?control_type=${TableData.Control_id}&version=${TableData.Version_id}`
        );

        if (response.status === 200) {
          setComplianceData(response.data.data.compliance);
        } else {
          console.error("Unexpected response:", response);
        }
      } catch (error) {
        console.error("Error fetching compliance data:", error);
        if (isFrameworkNotEnabledError(error)) setFrameworkDisabled(true);
      } finally {
        setIsTableLoading(false);
      }
    };

    if (TableData.Control_id && TableData.Version_id) {
      fetchData();
    }
  }, [trigger, TableData.Control_id, TableData.Version_id]);

  const truncateToTwoDecimals = (num: number) => Math.floor(num * 100) / 100;

  const [isSummaryData, setIsSummaryData] = useState<{
    total_count: number;
    passed_count: number;
    failed_count: number;
    compliance: number;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = {
          control_type: TableData.Control_id,
          version: TableData.Version_id,
        };
        const response = await axiosInstance.post(
          "/securityscan/cd-compliance-posture",
          payload
        );
        const complianceData = response.data?.data?.compliance_data;

        if (complianceData) {
          setIsSummaryData({
            total_count: complianceData.total_count,
            passed_count: complianceData.passed_count,
            failed_count: complianceData.failed_count,
            compliance: complianceData.compliance,
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (isFrameworkNotEnabledError(error)) setFrameworkDisabled(true);
      }
    };

    fetchData();
  }, [trigger, TableData.Control_id, TableData.Version_id]);

  const trendChartOptions = useMemo(
    () => ({
      chart: {
        type: "line" as const,
        height: 200,
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      colors: ["#6CBD54", "#F08B0E", "#02AFBD"], // Colors for IG1, IG2, IG3
      stroke: { curve: "smooth" as const, width: 3 },
      markers: {
        size: 4,
        hover: {
          sizeOffset: 2,
        },
      },
      xaxis: {
        categories: trendCategories,
        labels: {
          show: true,
          rotate: -30,
          style: {
            fontSize: "10px",
            colors: theme.palette.mode === "dark" ? "#fff" : "#000",
          },
          formatter: (val: string) => dayjs(val).format("DD/MMM/YY"),
          showDuplicates: false,
          trim: true,
          hideOverlappingLabels: true,
          maxHeight: 40,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        tooltip: {
          enabled: false, // Disable x-axis tooltip
        },
      },
      yaxis: {
        title: {
          text: "Compliance %",
          style: { color: theme.palette.text.secondary },
        },
        labels: {
          style: { colors: theme.palette.text.secondary },
          formatter: (val: number) => `${Math.round(val)}%`,
        },
        min: 0,
        max: 100,
      },
      legend: {
        position: "top" as const,
        horizontalAlign: "right" as const,
        labels: {
          colors: theme.palette.text.primary,
        },
      },
      tooltip: {
        theme: theme.palette.mode,
        shared: true,
        intersect: false,
        x: {
          show: true,
          formatter: (val: any, opts: any) => {
            return dayjs(trendCategories[val]).format("DD/MMM/YY");
          },
        },
        y: {
          formatter: (val: number) => `${val.toFixed(1)}%`,
        },
      },
      grid: {
        borderColor: theme.palette.divider,
        padding: {
          left: 15,
          right: 40,
          top: 5,
          bottom: 45,
        },
      },
    }),
    [trendCategories, theme]
  );

  if (frameworkDisabled) {
    return <FrameworkNotEnabled />;
  }

  return (
    <Box
      sx={{ bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit" }}
    >
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={12}>
          <Accordion
            expanded={isSummaryExpanded}
            onChange={() => setIsSummaryExpanded(!isSummaryExpanded)}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "4px",
              bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "white",
              "&.Mui-expanded": {
                marginBottom: 0,
              },
              "&:before": {
                display: "none",
              },
            }}
            disableGutters
            elevation={0}
          >
            {/* import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'; */}
            <AccordionSummary
              expandIcon={
                isSummaryExpanded ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowUpIcon />
                )
              }
              aria-controls="summary-content"
              id="summary-header"
              sx={{
                borderBottom: isSummaryExpanded
                  ? `1px solid ${theme.palette.divider}`
                  : "none",
                minHeight: "48px",
                flexDirection: "row-reverse",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(0deg)",
                },
                borderRadius: "8px 8px 0 0",
                fontWeight: 700,
                bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 3,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  SUMMARY
                </Typography>
                {!isSummaryExpanded && isSummaryData && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      ml: "auto",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Compliance:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 150,
                      }}
                    >
                      <Box sx={{ flexGrow: 1, width: "100%" }}>
                        <LinearProgress
                          variant="determinate"
                          value={Number(
                            (isSummaryData.compliance || 0).toFixed(2)
                          )}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            mr: 2,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "#333"
                                : "#f0f0f0",
                            "& .MuiLinearProgress-bar": {
                              bgcolor:
                                (isSummaryData.compliance || 0) >= 75
                                  ? "green"
                                  : (isSummaryData.compliance || 0) >= 50
                                    ? "orange"
                                    : "red",
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, minWidth: 45 }}
                      >
                        {Number((isSummaryData?.compliance || 0).toFixed(2))}%
                      </Typography>
                    </Box>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />
                    {/* Total Controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Total Controls :
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {isSummaryData.total_count}
                      </Typography>
                    </Box>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />
                    {/* Passed */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={checkMarkIcon}
                        alt="Passed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Passed:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#43a047" }}
                      >
                        {isSummaryData.passed_count}
                      </Typography>
                    </Box>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />
                    {/* Failed */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={unCheckMarkIcon}
                        alt="Failed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Failed:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#e53935" }}
                      >
                        {isSummaryData.failed_count}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 3,
                borderRadius: "0 0 8px 8px",
                height: "30vh",
                overflow: "hidden",
                background:
                  theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
              }}
            >
              {isLoading || !isSummaryData ? (
                // Skeleton Loader
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="stretch"
                  gap={2}
                  height="100%"
                >
                  <div
                    style={{
                      padding: 2,
                      flex: 0.3,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      sx={{
                        flex: 0.4,
                        minWidth: 120,
                        ml: -10,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                          ml: 9,
                        }}
                      >
                        <Skeleton variant="circular" width={140} height={140} />
                        {/* <Skeleton variant="text" width={150} height={32} /> */}
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        flex: 0.6,
                        ml: 2,
                        height: "100%",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ width: "fit-content" }}
                      >
                        <Box display="flex" alignItems="center" mb={1}>
                          <Skeleton variant="text" width={120} height={24} />
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Skeleton
                            variant="rectangular"
                            width={100}
                            height={24}
                          />
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Skeleton
                            variant="rectangular"
                            width={100}
                            height={24}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, my: 0 }}
                  />
                  <div
                    style={{
                      padding: 2,
                      flex: 0.7,
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Skeleton variant="text" width={100} height={28} />
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton variant="circular" width={10} height={10} />
                        <Skeleton variant="text" width={80} height={20} />
                        <Skeleton variant="text" width={40} height={20} />
                      </Box>
                    </Box>
                    <Skeleton variant="rectangular" width="100%" height={140} />
                  </div>
                </Box>
              ) : (
                // Actual Content
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="stretch"
                  gap={2}
                  height="100%"
                >
                  <div
                    style={{
                      padding: 2,
                      flex: 0.3,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      sx={{
                        flex: 0.4,
                        minWidth: 120,
                        ml: -10,
                      }}
                    >
                      <ReactApexChart
                        key={theme.palette.mode}
                        options={{
                          chart: {
                            type: "radialBar",
                            sparkline: { enabled: true },
                          },
                          plotOptions: {
                            radialBar: {
                              hollow: { size: "60%" },
                              dataLabels: {
                                name: {
                                  show: true,
                                  fontSize: "16px",
                                  fontWeight: 600,
                                  //color: "#8884d8",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#8884d8",
                                  offsetY: 24,
                                },
                                value: {
                                  fontSize: "22px",
                                  fontWeight: 600,
                                  offsetY: -10,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#696595",
                                },
                              },
                            },
                          },
                          fill: { colors: ["#6c7ba1"] },
                          stroke: { lineCap: "round" },
                          labels: ["Compliance%"],
                        }}
                        series={[
                          truncateToTwoDecimals(isSummaryData?.compliance || 0),
                        ]}
                        type="radialBar"
                        height={185}
                      />
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        flex: 0.6,
                        ml: 2,
                        height: "100%",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ width: "fit-content" }}
                      >
                        <Box display="flex" alignItems="center" mb={1}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 16,
                              mr: 1,
                            }}
                          >
                            Total Controls :
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            {isSummaryData?.total_count}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <img
                            src={checkMarkIcon}
                            alt="Passed"
                            style={{ width: 20, height: 20, marginRight: 10 }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 16,
                              mr: 3,
                            }}
                          >
                            Passed :
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#43a047",
                              fontSize: 16,
                            }}
                          >
                            {isSummaryData?.passed_count}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <img
                            src={unCheckMarkIcon}
                            alt="Failed"
                            style={{ width: 20, height: 20, marginRight: 10 }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 16,
                              mr: 3,
                            }}
                          >
                            Failed :
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#e53935",
                              fontSize: 16,
                            }}
                          >
                            {isSummaryData?.failed_count}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </div>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, my: 0 }}
                  />
                  <div
                    style={{
                      padding: 2,
                      flex: 0.7,
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16 }}
                      >
                        TRENDS
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "#3B82F6",
                          }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ fontSize: 14 }}
                        >
                          {version ? `${version} Compliance` : "Compliance"}
                        </Typography>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap="4px"
                          sx={{
                            backgroundColor: NIST_TREND_DATA.isPositive ? "#00A61B1C" : "#F03C3C1C",
                            borderRadius: "10px",
                            padding: "2px 6px",
                          }}
                        >
                          {NIST_TREND_DATA.isPositive ? (
                            <ArrowOutwardIcon sx={{ color: "#00A61B", fontSize: 16 }} />
                          ) : (
                            <CallReceivedIcon sx={{ color: "#F03C3C", fontSize: 16 }} />
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontSize: 14,
                              color: NIST_TREND_DATA.isPositive ? "#00A61B" : "#F03C3C"
                            }}
                          >
                            {NIST_TREND_DATA.percentChange}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <ReactApexChart
                      options={{
                        ...trendChartOptions,
                        chart: {
                          ...trendChartOptions.chart,
                          height: 140,
                        },
                        xaxis: {
                          ...trendChartOptions.xaxis,
                          categories: trendCategories,
                          labels: {
                            rotate: -30,
                            style: {
                              fontSize: "10px",
                              colors:
                                theme.palette.mode === "dark" ? "#fff" : "#000",
                            },
                            formatter: (val: string) =>
                              dayjs(val).format("MMM  'YY"),
                            showDuplicates: false,
                            trim: true,
                            hideOverlappingLabels: true,
                            maxHeight: 40,
                          },
                          tickAmount: 8,
                          tickPlacement: "on",
                          tooltip: {
                            enabled: false, // Disable x-axis tooltip
                          },
                        },
                        yaxis: {
                          ...trendChartOptions.yaxis,
                          labels: {
                            formatter: (val: number) => `${Math.round(val)}%`,
                            style: {
                              fontSize: "10px",
                              colors:
                                theme.palette.mode === "dark" ? "#fff" : "#000",
                            },
                          },
                          min: 0,
                          max: 100,
                          tickAmount: 4,
                        },
                        tooltip: {
                          theme: theme.palette.mode,
                          shared: true,
                          intersect: false,
                          x: {
                            show: true,
                            formatter: (val: any, opts: any) => {
                              return dayjs(trendCategories[val]).format(
                                "DD/MMM/YY"
                              );
                            },
                          },
                          y: {
                            formatter: (val: number) => `${val.toFixed(2)}%`,
                          },
                        },
                        grid: {
                          ...trendChartOptions.grid,
                          padding: {
                            left: 15,
                            right: 23,
                            top: 5,
                            bottom: 5,
                          },
                        },
                      }}
                      series={trendSeries}
                      type="line"
                      height={140}
                    />
                  </div>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      <Paper
        sx={{
          pb: 0.5,
          background: theme.palette.mode === "dark" ? "#1E1E1E" : "#fff",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* <Typography variant="h6" sx={{ fontWeight: 700, }}>
            Controls
          </Typography> */}
          {/* <Typography
            variant="body2"
            sx={{ color: "#1976d2", fontWeight: 700, cursor: "pointer" }}
          >
            Report
          </Typography> */}
        </Box>
        <Box
          sx={{
            height: isSummaryExpanded
              ? "calc(61vh - 110px)"
              : "calc(91vh - 110px)",
            width: "100%",
          }}
        >
          <AgGridTable
            data={TableData_isWidgetClicked}
            columnDefs={TableData_WidgetClicked__isTABLE_COL_DEFS}
            isLoading={isTableLoading}
            pagination={true}
            styles={agGridInlineStyles}
            showSearch={true}
            searchPlaceholder="Search controls..."
            height={
              isSummaryExpanded ? "calc(56vh - 108px)" : "calc(86vh - 107.5px)"
            }
            // styles={{
            //   "& .ag-header": {
            //     background: "#fff",
            //     fontWeight: 700,
            //     fontSize: 15,
            //   },
            //   "& .ag-row": { fontSize: 15, height: 56 },
            //   "& .ag-cell": { padding: "0px 0px" },
            //   "& .ag-header-cell": { padding: "0 8px" },
            // }}
            onRowClick={(row: any) => handleNistRowClick(row)}
          />
        </Box>
      </Paper>
      <NISTDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        row={selectedRow}
      />
    </Box>
  );
};

// Stage pill renderer for AgGrid
const stageColors: Record<string, string> = {
  Govern: "#FFD600",
  Recover: "#00E676",
  Respond: "#FF5252",
  Detect: "#FF9100",
  Identify: "#2979FF",
  Protect: "#7C4DFF",
};

function BenchmarksTable() {
  const [isrowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setIsLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  const CiSSelscteId = useSelector(
    (state: any) => state.ComplinaceTableSelectedRow?.CISComplianceSelectedRowID
  );
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setFrameworkDisabled(false);
        const response = await axiosInstance.get(
          `/securityscan/controls?control_id=${CiSSelscteId}`
        );
        setRowData(response.data.data.controls);
      } catch (error) {
        console.error("Error fetching controls:", error);
        if (isFrameworkNotEnabledError(error)) {
          setRowData([]);
          setFrameworkDisabled(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [trigger, CiSSelscteId]);

  // const rowData = isrowData?.map((control) => ({
  //   id: control.id,
  //   name: control.name,
  //   benchmarks: control.benchmarks.map((benchmark:any)=>
  //     benchmark.trim()),
  //   percentage: control.percentage,
  //   tags: control.tags.map((tag: any) => tag.trim()).join(", "),
  // }));

  const rowData = isrowData?.map((control) => ({
    id: control.id,
    name: control.name,
    benchmarks: control.benchmarks,
    percentage: control.percentage,
    tags: control.tags.map((tag: string) => tag.trim()).join(", "),
  }));

  const columnDefs: ColDef[] = [
    // {
    //   field: "benchmarks",
    //   headerName: "Benchmark",
    //   maxWidth: 300,
    //   // valueGetter: (params: any) => params.node.rowIndex + 1,
    //   cellRenderer: (params: any) => {
    //     const values = Array.isArray(params.value)
    //       ? params.value
    //       : [params.value];
    //     const benchmarkImages: Record<string, string> = {
    //       CIS: CIS_IMG,
    //       SPARK: Spark_Text_Img,
    //       NIST: NIST_IMG,
    //     };

    //     return (
    //       <div
    //         style={{
    //           display: "flex",
    //           justifyContent: "flex-start",
    //           alignItems: "center",
    //           gap: "6px",
    //         }}
    //       >
    //         {values.map((benchmark: string) =>
    //           benchmarkImages[benchmark] ? (
    //             <img
    //               key={benchmark}
    //               src={benchmarkImages[benchmark]}
    //               alt={benchmark}
    //               style={{
    //                 width: benchmark === "CIS" ? "30px" : "70px",
    //                 height: "40px",
    //                 objectFit: "contain",
    //               }}
    //             />
    //           ) : null
    //         )}
    //       </div>
    //     );
    //   },
    //   cellStyle: {
    //     display: "flex",
    //     justifyContent: "flex-start",
    //   },
    // },
    {
      field: "benchmarks",
      headerName: "Benchmark",
      filter: true,
      minWidth: 150,
      width: 200,
      cellClass: "custom-cell",
      cellRenderer: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1, cursor: "default", }}>
          {params.value?.includes("SPARK") && (
            <img
              src={isDarkMode ? spark_dark : sparkLogo}
              alt="spark"
              height="24"
            />
          )}
          {params.value?.includes("CIS") && (
            <img
              src={isDarkMode ? cis_dark : cis_light}
              alt="cis"
              height="24"
            />
          )}
          {params.value?.includes("NIST") && (
            <img
              src={isDarkMode ? nist_dark : nistLogo}
              alt="nist"
              height="12"
            />
          )}
        </Box>
      ),
      cellStyle: {
        display: "flex",
        alignItems: "center",
        curser: "default",
      },
    },

    {
      field: "name",
      headerName: "Recommendation",
      filter: true,
      minWidth: 200,
      flex: 1,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip title={value}>
            <span style={{ width: "100%", cursor: "default", }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "700",
                  textAlign: "left", // <-- align left
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
      cellStyle: {
        display: "flex",
        justifyContent: "flex-start", // <-- align left
        alignItems: "center",
      },
    },
    {
      field: "tags",
      headerName: "Tags",
      filter: true,
      minWidth: 80,
      width: 245,
      cellStyle: {
        display: "flex",
        curser: "default",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start", // <-- align left
      },
      cellRenderer: (params: any) => {
        const getRandomColor = () => {
          const hue = Math.floor(Math.random() * 360);
          return {
            background: `hsl(${hue}, 70%, 80%)`,
            color: `hsl(${hue}, 50%, 20%)`,
          };
        };

        const tagString = params.data?.tags || "";
        const tagsArray = tagString
          .split(",")
          .map((tag: any) => tag.trim())
          .filter(Boolean);

        const visibleTags = tagsArray.slice(0, 1);
        const hiddenTags = tagsArray.slice(1);

        return (
          <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
            {visibleTags.map((tag: string, index: number) => {
              let style = {};
              if (index === 0) {
                style = {
                  backgroundColor: "#0485C21A",
                  color: "#0485C2",
                };
              } else if (index === 1) {
                style = {
                  backgroundColor: "#67000424",
                  color: "#670004",
                };
              } else {
                const random = getRandomColor();
                style = {
                  backgroundColor: random.background,
                  color: random.color,
                };
              }

              return (
                <Chip
                  key={index}
                  label={tag}
                  style={{
                    cursor: "default",
                    ...style,
                    fontWeight: "bold",
                    borderRadius: 4,
                  }}
                />
              );
            })}

            {hiddenTags.length > 0 && (
              <WhiteTooltip title={hiddenTags.join(", ")} arrow placement="top">
                <div
                  style={{
                    backgroundColor: "#ccc",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: 14,
                    cursor: "default",
                  }}
                >
                  +{hiddenTags.length}
                </div>
              </WhiteTooltip>
            )}
          </div>
        );
      },
    },

    {
      field: "percentage",
      headerName: "Compliance",
      filter: true,
      minWidth: 120,
      cellRenderer: (params: any) => {
        const value = Number(params.value) || 0;

        // Choose progress color based on value
        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "default",
              justifyContent: "flex-start", // <-- align left
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: 40,
                height: 40,
              }}
            >
              {/* Background Circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{
                  color: "#c5c5d2", // background circle
                }}
              />
              {/* Foreground Progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
              {`${value}%`}
            </Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  return (
    <AgGridTable
      columnDefs={columnDefs}
      data={rowData}
      showSearch
      isRowSelectable
      // pagination={true}
      height={"calc(77vh - 110px)"}
      isLoading={isLoading}
      noRowsMessage={frameworkDisabled ? FRAMEWORK_NOT_ENABLED_MESSAGE : undefined}
    />
  );
}

import Cisco_img from "../assets/cisco-assets.svg";
import arista_assets from "../assets/arista-assets.svg";
import cisco_assets from "../assets/cisco-assets.svg";
import cisco_dark from "../assets/cisco-white.svg";
import juniper_dark from "../assets/juniper-white.svg";
import juniperLogo from "../assets/juniper.svg";
import paloalto_assets from "../assets/paloalto-assets.svg";
import paloalto_dark from "../assets/paloalto-white.svg";
import ubuntu_white from "../assets/ubuntu_white.svg";
import ubuntu_dark from "../assets/ubuntu_dark.svg";
import windows_light from "../assets/baseVersion/Windows_light.svg";
import windows_dark from "../assets/baseVersion/Windows_dark.svg";
import debian from "../assets/debian.svg";
import debian_dark from "../assets/debian_dark.svg";
import redhat_light from "../assets/redhat_light.svg";
import redhat_dark from "../assets/redhat_dark.svg";
import huawei_light from "../assets/huawei-ar21 1.svg";
import huawei_dark from "../assets/huawei-dark.svg";
import mikrotik from "../assets/mikrotik.svg";
import mikrotik_dark from "../assets/mikrotik_dark.svg";
import edgecore_logo from "../assets/baseVersion/edgecore-logo.svg";
import tplink_light from "../assets/baseVersion/TPLINK_Logo_2 1.svg";
import tplink_dark from "../assets/baseVersion/TPLINK_Logo_dark.svg";
import fortinet_img from "../assets/fortinet.svg";
import fortinet_scan_dark from "../assets/fortinet_scan_dark.svg";
import sophosLogo from "../assets/sophos.svg";
import sophos_dark from "../assets/sophos_dark.svg";
import apple_img from "../assets/apple.svg";
import apple_dark from "../assets/apple_dark.svg";
import dell_logo from "../assets/baseVersion/dell-seeklogo 2.svg";

import syrotech_light from "../assets/syrotech_light.svg";
import syrotech_dark from "../assets/syrotech_dark.svg";
function AssetsTable() {
  const [isrowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);

  const CiSSelscteId = useSelector(
    (state: any) => state.ComplinaceTableSelectedRow?.CISComplianceSelectedRowID
  );
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setIsLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setFrameworkDisabled(false);
        const response = await axiosInstance.get(
          `/securityscan/compliance-assets?control_id=${CiSSelscteId}&module=on-prem`
        );
        setRowData(response.data.data.compliance_assets);
      } catch (error) {
        console.error("Error fetching controls:", error);
        if (isFrameworkNotEnabledError(error)) {
          setRowData([]);
          setFrameworkDisabled(true);
        }
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [trigger, CiSSelscteId]);

  const rowData = isrowData?.map((control) => ({
    assets: control.hostname,
    vendor: control.vendor,
    device_type: control.device_type,
    type: `${control.vendor} - ${control.device_type}`,
    compliance: control.compliance,
    percentage:
      control.recommendations > 0
        ? (control.passed_recommendations / control.recommendations) * 100
        : 0,
    passed_recommendations: control.passed_recommendations,
    recommendations: control.recommendations,
  }));

  const columnDefs: ColDef[] = [
    {
      field: "assets",
      headerName: "Name",
      maxWidth: 200,
      // valueGetter: (params: any) => params.node.rowIndex + 1,
    },

    {
      field: "type",
      headerName: "Type",
      filter: true,
      minWidth: 140,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      cellRenderer: (params: any) => {
        const vendor = params.data?.vendor?.toLowerCase();

        const getImage = () => {
          if (vendor === "cisco") {
            return (
              <img
                src={isDarkMode ? cisco_dark : cisco_assets}
                alt="Cisco"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "juniper") {
            return (
              <img
                src={isDarkMode ? juniper_dark : juniperLogo}
                alt="Juniper"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "palo alto" || vendor === "paloalto") {
            return (
              <img
                src={isDarkMode ? paloalto_dark : paloalto_assets}
                alt="Palo Alto"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "arista") {
            return (
              <img
                src={isDarkMode ? arista_dark : arista_assets}
                alt="Arista"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "fortinet") {
            return (
              <img
                src={isDarkMode ? fortinet_scan_dark : fortinet_img}
                alt="Fortinet"
                style={{ width: "50px", height: "24px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "ubuntu") {
            return (
              <img
                src={isDarkMode ? ubuntu_dark : ubuntu_white}
                alt="Ubuntu"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "microsoft" || vendor === "windows") {
            return (
              <img
                src={isDarkMode ? windows_dark : windows_light}
                alt="Microsoft"
                style={{ width: "65px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "redhat" || vendor === "rhel" || vendor === "red hat") {
            return (
              <img
                src={isDarkMode ? redhat_dark : redhat_light}
                alt="RedHat"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "debian") {
            return (
              <img
                src={isDarkMode ? debian_dark : debian}
                alt="Debian"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "huawei") {
            return (
              <img
                src={isDarkMode ? huawei_dark : huawei_light}
                alt="Huawei"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "mikrotik") {
            return (
              <img
                src={isDarkMode ? mikrotik_dark : mikrotik}
                alt="Mikrotik"
                style={{ width: "90px", height: "35px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "edgecore") {
            return (
              <img
                src={edgecore_logo}
                alt="Edgecore"
                style={{ width: "80px", height: "30px", objectFit: "contain", filter: isDarkMode ? "brightness(0) invert(1)" : "none" }}
              />
            );
          }
          if (vendor === "tp-link" || vendor === "tplink") {
            return (
              <img
                src={isDarkMode ? tplink_dark : tplink_light}
                alt="TP-Link"
                style={{ width: "80px", height: "30px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "dell") {
            return (
              <img
                src={dell_logo}
                alt="Dell"
                style={{ width: "40px", height: "40px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "syrotech") {
            return (
              <img
                src={isDarkMode ? syrotech_dark : syrotech_light}
                alt="Syrotech"
                style={{ width: "90px", height: "32px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "sophos") {
            return (
              <img
                src={isDarkMode ? sophos_dark : sophosLogo}
                alt="Sophos"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "apple" || vendor === "macos") {
            return (
              <img
                src={isDarkMode ? apple_dark : apple_img}
                alt="Apple"
                style={{ width: "28px", height: "28px", objectFit: "contain" }}
              />
            );
          }
          return null;
        };

        const image = getImage();
        return (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            {image || (
              <Typography
                title={params.data?.vendor}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {params.data?.vendor}
              </Typography>
            )}
          </Box>
        );
      },
    },

    {
      field: "compliance",
      headerName: "Compliance",
      filter: true,
      minWidth: 50,
      cellRenderer: (params: any) => {
        const value = Math.round(Number(params.value)) || 0;

        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 30,
                height: 30,
              }}
            >
              {/* Background circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{ color: "#c5c5d2" }}
              />
              {/* Foreground progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                  top: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {`${value}%`}
            </Typography>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      // cellRenderer: (params: any) => {
      //   const isManual = params.value === "Manual";
      //   const color = isManual ? "#F7941F" : "#696595";

      //   return (
      //     <div
      //       style={{
      //         border: `1px solid ${color}`,
      //         borderRadius: 4,
      //         padding: "2px 6px",
      //         lineHeight: "1.5",
      //         whiteSpace: "nowrap",
      //         fontSize: 12,
      //         color: color,
      //       }}
      //     >
      //       {params.value}
      //     </div>
      //   );
      // },
    },

    {
      field: "percentage",
      headerName: "Benchmark",
      filter: true,
      minWidth: 100,
      cellRenderer: (params: any) => {
        const passedCount = params.data.passed_recommendations;
        const compliance_count = params.data.recommendations;
        const progressValue = params.data.percentage || 0;

        return (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#f5f5f5",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        progressValue >= 70
                          ? "#4caf50"
                          : progressValue >= 40
                            ? "#ff9800"
                            : "#f44336",
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                {`${passedCount}/${compliance_count}`}
              </Typography>
            </Box>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  return (
    <AgGridTable
      columnDefs={columnDefs}
      data={rowData}
      showSearch
      isRowSelectable
      height={"calc(77vh - 110px)"}
      isLoading={isLoading}
      noRowsMessage={frameworkDisabled ? FRAMEWORK_NOT_ENABLED_MESSAGE : undefined}
    />
  );
}

function ResourceTable() {
  const [isrowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);

  const CiSSelscteId = useSelector(
    (state: any) => state.ComplinaceTableSelectedRow?.CISComplianceSelectedRowID
  );

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setIsLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setFrameworkDisabled(false);
        const response = await axiosInstance.get(
          `/securityscan/compliance-assets?control_id=${CiSSelscteId}&module=cloud`
        );
        setRowData(response.data.data.compliance_assets);
      } catch (error) {
        console.error("Error fetching controls:", error);
        if (isFrameworkNotEnabledError(error)) {
          setRowData([]);
          setFrameworkDisabled(true);
        }
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [trigger, CiSSelscteId]);

  const rowData = isrowData?.map((control) => ({
    assets: control.hostname,
    vendor: control.vendor,
    device_type: control.device_type,
    type: `${control.vendor} - ${control.device_type}`,
    compliance: control.compliance,
    percentage:
      control.recommendations > 0
        ? (control.passed_recommendations / control.recommendations) * 100
        : 0,
    passed_recommendations: control.passed_recommendations,
    recommendations: control.recommendations,
  }));

  const columnDefs: ColDef[] = [
    {
      field: "assets",
      headerName: "Name",
      maxWidth: 200,
      // valueGetter: (params: any) => params.node.rowIndex + 1,
    },

    {
      field: "type",
      headerName: "Type",
      filter: true,
      minWidth: 200,
      cellRenderer: (params: any) => {
        const vendor = params.data.vendor;
        const deviceType = params.data.device_type;
        const isCisco = vendor?.toLowerCase() === "cisco";
        const typeLabel = `${vendor} - ${deviceType}`;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isCisco && (
              <img
                src={Cisco_img}
                alt="Cisco"
                style={{ width: 30, height: 30 }}
              />
            )}
            <Typography variant="body2">{typeLabel}</Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },

    {
      field: "compliance",
      headerName: "Compliance",
      filter: true,
      minWidth: 50,
      cellRenderer: (params: any) => {
        const value = Number(params.value) || 0;

        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 30,
                height: 30,
              }}
            >
              {/* Background circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{ color: "#c5c5d2" }}
              />
              {/* Foreground progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                  top: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {`${value}%`}
            </Typography>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      // cellRenderer: (params: any) => {
      //   const isManual = params.value === "Manual";
      //   const color = isManual ? "#F7941F" : "#696595";

      //   return (
      //     <div
      //       style={{
      //         border: `1px solid ${color}`,
      //         borderRadius: 4,
      //         padding: "2px 6px",
      //         lineHeight: "1.5",
      //         whiteSpace: "nowrap",
      //         fontSize: 12,
      //         color: color,
      //       }}
      //     >
      //       {params.value}
      //     </div>
      //   );
      // },
    },

    {
      field: "percentage",
      headerName: "Benchmark",
      filter: true,
      minWidth: 100,
      cellRenderer: (params: any) => {
        const passedCount = params.data.passed_recommendations;
        const compliance_count = params.data.recommendations;
        const progressValue = params.data.percentage || 0;

        return (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#f5f5f5",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        progressValue >= 70
                          ? "#4caf50"
                          : progressValue >= 40
                            ? "#ff9800"
                            : "#f44336",
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                {`${passedCount}/${compliance_count}`}
              </Typography>
            </Box>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  return (
    <AgGridTable
      columnDefs={columnDefs}
      data={rowData}
      showSearch
      isRowSelectable
      height={"calc(70vh - 110px)"}
      isLoading={isLoading}
      noRowsMessage={frameworkDisabled ? FRAMEWORK_NOT_ENABLED_MESSAGE : undefined}
    />
  );
}

function ControlDetailsDrawer({
  open,
  onClose,
  row,
}: {
  open: boolean;
  onClose: () => void;
  row: any;
}) {
  const [tab, setTab] = useState(0); // Always call hooks first
  const [isComplianceHedaer, setIsComplinaceHedaer] = useState([]);
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(0);
      setShowFullDescription(false);
    }
  }, [open]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  console.log("COMPLIANCECISrow", row);

  const dispatch = useDispatch();
  dispatch(setComplianceTableRowId({ CISComplianceSelectedRowID: row?.id }));

  useEffect(() => {
    if (!row) return;

    const fetchComplianceDetails = async () => {
      try {
        setFrameworkDisabled(false);
        const response = await axiosInstance.get(
          `/securityscan/compliance-details?control_id=${row?.id}`
        );

        setIsComplinaceHedaer(response?.data?.data?.compliance);

        console.log("Success:", response?.data?.data?.compliance);
      } catch (error) {
        console.error("Error fetching compliance details:", error);
        if (isFrameworkNotEnabledError(error)) {
          setIsComplinaceHedaer([]);
          setFrameworkDisabled(true);
        }
      }
    };

    fetchComplianceDetails();
  }, [trigger, open, row?.id]);

  if (!row) return null;

  if (frameworkDisabled) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ "& .MuiDrawer-paper": { width: "55%", p: 1 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <IconButton onClick={onClose}>
            <CancelIcon />
          </IconButton>
        </Box>
        <FrameworkNotEnabled />
      </Drawer>
    );
  }

  const HeaderData = (
    isComplianceHedaer as {
      id: string;
      name: string;
      description: string;
      percentage: number;
      controls_count: number;
      asset_count: number;
      resource_count?: number;
      type: string[];
      stage: string;
    }[]
  )?.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description,
    percentage: item.percentage,
    controls_count: item.controls_count,
    asset_count: item.asset_count,
    resource_count: item.resource_count, // <-- add this line
    type: item.type,
    stage: item.stage,
  }));

  const getChipStyle = (type: string) => {
    switch (type) {
      case "IG1":
        return { backgroundColor: "#6CBD54", color: "white" };
      case "IG2":
        return { backgroundColor: "#F08B0E", color: "white" };
      case "IG3":
        return { backgroundColor: "#02AFBD", color: "white" };
      default:
        return { backgroundColor: "#e0e0e0", color: "black" };
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "55%",
          // maxWidth: 700,
          p: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: 2,
          mt: 1,
        }}
      >
        <Box>
          <Typography
            component="span"
            sx={{
              color: "#1976d2",
              fontWeight: 500,
              fontSize: "16px",
              backgroundColor: "#B6B6B626",
              p: 0.5,
            }}
          >
            {row.controlId}
          </Typography>
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontSize: "16px", ml: 1 }}
          >
            {HeaderData[0]?.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CancelIcon />
        </IconButton>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#716DA0",
            fontWeight: 400,
            fontSize: "14px",
            mb: 1,
            ml: 2,
            whiteSpace: "pre-line",
            display: "-webkit-box",
            WebkitLineClamp: showFullDescription ? "unset" : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {HeaderData[0]?.description || "No description available"}
        </Typography>

        {HeaderData[0]?.description?.length > 225 && (
          <Typography
            variant="body2"
            color="primary"
            sx={{
              cursor: "pointer",
              mb: 3,
              ml: 2,
              color: isDarkMode ? "#FFFFFF" : "#3A2ADA",
            }}
            onClick={() => setShowFullDescription(!showFullDescription)}
          >
            {showFullDescription ? "Show less" : "Show more"}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={1.5} mb={3} mt={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", ml: 2 }}>
              Compliance%:
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                whiteSpace: "nowrap",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 30,
                  height: 30,
                }}
              >
                {/* Background circle */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={30}
                  thickness={5}
                  sx={{ color: "#c5c5d2" }}
                />
                {/* Foreground progress */}
                <CircularProgress
                  variant="determinate"
                  value={HeaderData[0]?.percentage || 0}
                  size={30}
                  thickness={5}
                  sx={{
                    color:
                      (HeaderData[0]?.percentage || 0) >= 75
                        ? "green"
                        : (HeaderData[0]?.percentage || 0) >= 50
                          ? "orange"
                          : "red",
                    position: "absolute",
                    left: 0,
                  }}
                />
              </Box>
              {/* <Typography variant="body2" fontWeight={600}>
                          {`${value}%`}
                        </Typography> */}
            </Box>

            <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
              {HeaderData[0]?.percentage ?? 0}%
            </Typography>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          />

          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
              IG Type:
            </Typography>
            <Stack direction="row" spacing={1}>
              <span style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {HeaderData[0]?.type.map((type: string, index: number) => (
                  <Chip
                    key={index}
                    label={type}
                    size="small"
                    sx={{
                      ...getChipStyle(type),
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      borderRadius: "4px",
                    }}
                  />
                ))}
              </span>
            </Stack>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          />

          <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
            Total Controls:{" "}
            <span style={{ fontWeight: 800, fontSize: 16 }}>
              {HeaderData[0]?.controls_count}
            </span>
          </Typography>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          />

          <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
            Assets:{" "}
            <span style={{ fontWeight: 700, fontSize: "16px" }}>
              {HeaderData[0]?.asset_count}
            </span>
          </Typography>
        </Box>
        <Divider />

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, height: "29px" }}
        >
          <Tab label="Benchmarks" sx={{ fontSize: "13px", fontWeight: 600 }} />
          <Tab label="Assets" sx={{ fontSize: "13px", fontWeight: 600 }} />
          {HeaderData[0]?.resource_count && (
            <Tab label="Resources" sx={{ fontSize: "13px", fontWeight: 600 }} />
          )}
        </Tabs>
        {tab === 0 && <BenchmarksTable />}
        {tab === 1 && <AssetsTable />}
        {tab === 2 && <ResourceTable />}
      </Box>
    </Drawer>
  );
}

import NistAssetsTable from "../pages/NistAssetsTable";
import NistBenchmarksTable from "../pages/NistBenchmarksTable";
import NistResourcesTable from "../pages/NistResourcesTable";
import PciBenchmarksTable from "./SecurityPosture/PciBenchmarksTable";
import PciAssetsTable from "./SecurityPosture/PciAssetsTable";
import PciResourcesTable from "./SecurityPosture/PciResourceTable";
import { filter, max } from "lodash";
import eventEmitter from "../components/utils/eventEmitter";
import WhiteTooltip from "../components/WhiteTooltip";
import { maxWidth, minWidth, style } from "@mui/system";
function NISTDetailsDrawer({
  open,
  onClose,
  row,
}: {
  open: boolean;
  onClose: () => void;
  row: any;
}) {
  const [tab, setTab] = useState(0); // Always call hooks first
  const [isComplianceHedaer, setIsComplinaceHedaer] = useState([]);
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(0);
      setShowFullDescription(false);
    }
  }, [open]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  console.log(row, "NISTrow");
  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    if (!row) return;

    const fetchComplianceDetails = async () => {
      try {
        setFrameworkDisabled(false);
        const response = await axiosInstance.get(
          `/securityscan/compliance-details?control_id=${row?.id}`
        );

        setIsComplinaceHedaer(response?.data?.data?.compliance);

        console.log("Success:", response?.data?.data?.compliance);
      } catch (error) {
        console.error("Error fetching compliance details:", error);
        if (isFrameworkNotEnabledError(error)) {
          setIsComplinaceHedaer([]);
          setFrameworkDisabled(true);
        }
      }
    };

    fetchComplianceDetails();
  }, [trigger, open, row?.id]);

  if (!row) return null;

  if (frameworkDisabled) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ "& .MuiDrawer-paper": { width: "55%", p: 1 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <IconButton onClick={onClose}>
            <CancelIcon />
          </IconButton>
        </Box>
        <FrameworkNotEnabled />
      </Drawer>
    );
  }

  const HeaderData = (
    isComplianceHedaer as {
      id: string;
      name: string;
      description: string;
      percentage: number;
      controls_count: number;
      asset_count: number;
      resource_count?: number;
      type: string[];
      stage: string;
    }[]
  )?.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description,
    percentage: item.percentage,
    controls_count: item.controls_count,
    asset_count: item.asset_count,
    resource_count: item.resource_count, // <-- add this line
    type: item.type,
    stage: item.stage,
  }));

  const getChipStyle = (type: string) => {
    switch (type) {
      case "Recover":
        return {
          backgroundColor: "#00A61B1A", // light green
          color: "#00A61B",
          border: "1px solid #00A61B",
        };
      case "Govern":
        return {
          backgroundColor: "#E5A8001A", // light orange
          color: "#E5A800",
          border: "1px solid #E5A800",
        };
      case "Respond":
        return {
          backgroundColor: "#F53B3B1A", // light teal
          color: "#F53B3B",
          border: "1px solid #F53B3B",
        };
      case "Detect":
        return {
          backgroundColor: "#F7941F1A", // light teal
          color: "#F7941F",
          border: "1px solid #F7941F",
        };
      case "Identify":
        return {
          backgroundColor: "#0485C21F", // light teal
          color: "#0485C2",
          border: "1px solid #0485C2",
        };
      case "Protect":
        return {
          backgroundColor: "#682EFA1F", // light teal
          color: "#682EFA",
          border: "1px solid #682EFA",
        };
      default:
        return {
          backgroundColor: "rgba(224, 224, 224, 0.3)",
          color: "black",
          border: "1px solid #e0e0e0",
        };
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "55%",
          // maxWidth: 700,
          p: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: 2,
          mt: 1,
        }}
      >
        <Box>
          <Typography
            component="span"
            sx={{
              color: "#1976d2",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#B6B6B626",
              p: 0.5,
            }}
          >
            {row.controlId}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              ml: 1,
              // whiteSpace: "pre-line",
            }}
          >
            {HeaderData[0]?.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CancelIcon />
        </IconButton>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#716DA0",
            fontWeight: 400,
            fontSize: "14px",
            mb: 1,
            ml: 2,
            whiteSpace: "pre-line",
            display: "-webkit-box",
            WebkitLineClamp: showFullDescription ? "unset" : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {HeaderData[0]?.description || "No description available"}
        </Typography>

        {HeaderData[0]?.description?.length > 225 && (
          <Typography
            variant="body2"
            color="primary"
            sx={{
              cursor: "pointer",
              mb: 2,
              ml: 2,
              color: isDarkMode ? "#FFFFFF" : "#3A2ADA",
            }}
            onClick={() => setShowFullDescription(!showFullDescription)}
          >
            {showFullDescription ? "Show less" : "Show more"}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={1.5} mb={3} mt={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{ fontWeight: 500, fontSize: "12px", ml: 2, mr: 2 }}
            >
              Compliance%:
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                whiteSpace: "nowrap",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 30,
                  height: 30,
                }}
              >
                {/* Background circle */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={30}
                  thickness={5}
                  sx={{ color: "#c5c5d2" }}
                />
                {/* Foreground progress */}
                <CircularProgress
                  variant="determinate"
                  value={HeaderData[0]?.percentage || 0}
                  size={30}
                  thickness={5}
                  sx={{
                    color:
                      (HeaderData[0]?.percentage || 0) >= 75
                        ? "green"
                        : (HeaderData[0]?.percentage || 0) >= 50
                          ? "orange"
                          : "red",
                    position: "absolute",
                    left: 0,
                  }}
                />
              </Box>
              {/* <Typography variant="body2" fontWeight={600}>
                          {`${value}%`}
                        </Typography> */}
            </Box>

            <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
              {HeaderData[0]?.percentage ?? 0}%
            </Typography>
          </Box>

          {/* <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          /> */}

          {/* <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
              Stage:
            </Typography>
            <Stack direction="row" spacing={1}>
              <span style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                <Chip
                  label={HeaderData[0]?.stage ?? ""}
                  variant="outlined"
                  style={getChipStyle(HeaderData[0]?.stage ?? "")}
                  sx={{
                    borderRadius: "16px",
                    fontWeight: "bold",
                    fontSize: "10px",
                  }}
                />
              </span>
            </Stack>
          </Box> */}

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          />

          <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
            Total Recommendations:{" "}
            <span style={{ fontWeight: 600, fontSize: "16px", marginLeft: 3 }}>
              {HeaderData[0]?.controls_count}
            </span>
          </Typography>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          />

          <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
            Assets:{" "}
            <span style={{ fontWeight: 600, fontSize: "16px", marginLeft: 2 }}>
              {HeaderData[0]?.asset_count}
            </span>
          </Typography>

          {HeaderData &&
            HeaderData[0] !== undefined &&
            typeof HeaderData[0].resource_count === "number" &&
            HeaderData[0].resource_count > 0 && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    mx: 1,
                    borderColor: "#D2D2F6",
                    height: 24,
                    alignSelf: "center",
                  }}
                />
                <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                  Resources:{" "}
                  <span style={{ fontWeight: 600, fontSize: "16px" }}>
                    {HeaderData[0].resource_count}
                  </span>
                </Typography>
              </>
            )}
        </Box>
        <Divider />

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab
            label="Recommendations"
            sx={{ fontSize: "13px", fontWeight: 600 }}
          />
          <Tab label="Assets" sx={{ fontSize: "13px", fontWeight: 600 }} />
          {HeaderData[0]?.resource_count && (
            <Tab label="Resources" sx={{ fontSize: "13px", fontWeight: 600 }} />
          )}
        </Tabs>
        {tab === 0 && <NistBenchmarksTable rowid={row?.id} />}
        {tab === 1 && <NistAssetsTable rowid={row?.id} />}
        {tab === 2 && <NistResourcesTable rowid={row?.id} />}
      </Box>
    </Drawer>
  );
}

const CisDetailsDashboard: React.FC = () => {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const location = useLocation();
  const lastSegment = decodeURIComponent(
    location.pathname.split("/").filter(Boolean).pop() || ""
  );
  const theme = useTheme();
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [complianceIGArray, setComplianceIGArray] = useState([]);
  const [trendSeries, setTrendSeries] = useState<any[]>([]);
  // const [summaryPoints, setSummaryPoints] = useState<any[]>([]);
  const [trendCategories, setTrendCategories] = useState<any[]>([]);
  const TableData = useSelector((state: any) => state.ComplianceWidget);
  const searchParams = new URLSearchParams(location.search);
  const versionType = searchParams.get("type");
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);
  // Set when the backend refuses this framework (switched off for the organization)
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);
  const [isSummaryData, setIsSummaryData] = useState<{
    total_count: number;
    passed_count: number;
    failed_count: number;
    compliance: number;
  } | null>(null);

  console.log(selectedRow, "selectedRowselectedRow");

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component is mounted
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchTrendData = async () => {
      setIsLoading(true);
      try {
        const today = dayjs().format("YYYY-MM-DD");
        const sevenDaysAgo = dayjs().subtract(7, "day").format("YYYY-MM-DD");
        const payload = {
          device_id: TableData?.Device_id || TableData?.device_id,
          duration: "custom_date_range",
          start_date: sevenDaysAgo,
          end_date: today,
          framework_data: versionType ? versionType.replace(/\s+/, "_") : "", // replace spaces with underscores, handle undefined
          is_compliance: true,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        const response = await axiosInstance.post(
          END_POINTS.DASHBOARD_COMPLIANCE,
          payload
        );
        if (response.status === 200 && response.data?.data?.graph_data) {
          const graphData = response.data.data.graph_data as any[];
          console.log("Graph Data:", graphData);

          // Set categories (dates)
          setTrendCategories(graphData.map((item) => item.date_time));

          // Dynamically extract series keys (e.g., Implementation Groups, SPARK 1, etc.)
          const excludedKeys = ["date_time", "device_count", "new_event", "date", "time"];
          const keysSet = new Set<string>();
          graphData.forEach((item: any) => {
            Object.keys(item).forEach((key) => {
              if (!excludedKeys.includes(key)) {
                keysSet.add(key);
              }
            });
          });

          const keys = Array.from(keysSet);
          if (keys.length > 0) {
            const series = keys.map((key) => ({
              name: key,
              data: graphData.map((item: any) => item[key] ?? 0),
            }));
            setTrendSeries(series);
          } else {
            setTrendSeries([]);
          }
        }
      } catch (error) {
        console.error("Error fetching compliance trend data:", error);
        if (isFrameworkNotEnabledError(error)) setFrameworkDisabled(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrendData();
  }, [trigger, versionType, TableData?.Control_id, TableData?.Version_id]);

  const IG_COLORS: Record<string, string> = {
    "Implementation Group 1": "#6AB852", // green
    "Implementation Group 2": "#F08B0E", // orange
    "Implementation Group 3": "#02AFBD", // teal
  };

  const IG_LABELS: Record<string, string> = {
    "Implementation Group 1": "IG1",
    "Implementation Group 2": "IG2",
    "Implementation Group 3": "IG3",
  };

  interface SummaryPoint {
    name: string;
    first: number;
    last: number;
    diff: string;
    isPositive: boolean;
  }

  interface SummaryUIProps {
    summaryPoints: SummaryPoint[];
    isDarkMode: boolean;
  }

  const summaryPoints = useMemo(() => {
    if (!trendSeries || trendSeries.length === 0) return [];

    return trendSeries.map((seriesItem) => {
      const data = seriesItem.data;
      const first = data?.[0] ?? 0;
      const last = data?.[data.length - 1] ?? 0;
      const diff = last - first;
      const isPositive = diff >= 0;

      return {
        name: seriesItem.name, // IG1, IG2, IG3
        first,
        last,
        diff: Math.abs(diff).toFixed(1),
        isPositive,
      };
    });
  }, [trendSeries]);

  const SummaryUI: React.FC<SummaryUIProps> = ({
    summaryPoints,
    isDarkMode,
  }) => {
    return (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="16px"
        mt={2}
      >
        {summaryPoints.map((item, idx) => {
          const label = IG_LABELS[item.name] || item.name;
          const color = IG_COLORS[item.name] || "#999";
          const Icon = item.isPositive ? ArrowOutwardIcon : CallReceivedIcon;
          const bgColor = item.isPositive ? "#00A61B1C" : "#F03C3C1C";
          const iconColor = item.isPositive ? "#00A61B" : "#F03C3C";
          const valueLabel = `${item.isPositive ? "" : "-"}${item.diff}%`;

          return (
            <Box
              key={idx}
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="8px"
            >
              {/* Color dot and label */}
              <FiberManualRecordIcon sx={{ color, fontSize: 16 }} />
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                {label}
              </Typography>

              {/* Badge with icon and % */}
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="5px"
                sx={{
                  backgroundColor: bgColor,
                  borderRadius: "10px",
                  padding: "2px 6px",
                }}
              >
                <Icon sx={{ color: iconColor, fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: iconColor,
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {valueLabel}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const [complianceData, setComplianceData] = useState([]);

  const TableData_WidgetClicked = complianceData?.map(
    (item: any, index: number) => ({
      number: index + 1,
      id: item.id,
      controlId: item.control_id,
      name: item.name,
      description: item.description,
      igType: item.type, // This is already an array, like ["IG2", "IG3"]
      compliance: item.percentage,
      recommendations: item.controls_count,
    })
  );
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      "& .ag-body-vertical-scroll": {
        width: "0px !important",
        minWidth: "0px !important",
      },
      "& .ag-body-vertical-scroll-viewport": {
        width: "0px !important",
        minWidth: "0px !important",
      },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  const TableData_WidgetClicked_TABLE_COL_DEFS = [
    { headerName: "#", field: "number", minWidth: 80, maxWidth: 100, width: 90 },
    {
      headerName: "Control ID",
      field: "controlId",
      filter: true,
      sortable: true,
      minWidth: 130,
      maxWidth: 150,
      cellStyle: {
        display: "flex",
        // justifyContent: "center",
        alignItems: "center",
      },
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip title={value}>
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "bold",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
    },
    {
      headerName: "Name",
      field: "name",
      filter: true,
      sortable: true,
      flex: 1,
      minWidth: 300,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip title={value}>
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "600",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      headerName: "Description",
      field: "description",
      flex: 2,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip
            title={
              <div
                className="scroll"
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  whiteSpace: "pre-line",
                  paddingRight: 8,
                }}
              >
                {value}
              </div>
            }
          >
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "600",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },

    {
      headerName: "IG Type",
      field: "igType",
      cellRenderer: (params: any) => {
        const rawValue = params.value; // This is the value passed from the cell renderer

        // Normalize the value to an array
        let values: string[] = [];

        if (Array.isArray(rawValue)) {
          values = rawValue.map((v) => String(v).trim());
        } else if (typeof rawValue === "string") {
          values = rawValue.split(",").map((v) => v.trim());
        } else if (rawValue != null) {
          values = [String(rawValue)];
        }

        const getChipStyle = (type: string) => {
          switch (type) {
            case "IG1":
              return { backgroundColor: "#6CBD54", color: "white" };
            case "IG2":
              return { backgroundColor: "#F08B0E", color: "white" };
            case "IG3":
              return { backgroundColor: "#02AFBD", color: "white" };
            default:
              return { backgroundColor: "#e0e0e0", color: "black" };
          }
        };

        return (
          <span style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {values.map((type, index) => (
              <Chip
                key={index}
                label={type}
                size="small"
                sx={{
                  ...getChipStyle(type),
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  borderRadius: "4px",
                }}
              />
            ))}
          </span>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      headerName: "Compliance %",
      field: "compliance",
      sortable: true,
      cellRenderer: (params: any) => {
        const value = Number(params.value) || 0;

        // Choose progress color based on value
        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: 40,
                height: 40,
              }}
            >
              {/* Background Circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{
                  color: "#c5c5d2", // background circle
                }}
              />
              {/* Foreground Progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
              {`${value}%`}
            </Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      headerName: "Recommendations",
      field: "recommendations",
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <span style={{ width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
                width: "100%",
                fontWeight: "600",
              }}
            >
              {value}
            </Typography>
          </span>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  const dispatch = useDispatch();



  useEffect(() => {
    setIsTableLoading(true);
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get(
          `/securityscan/cd-compliance?control_type=${TableData.Control_id}&version=${TableData.Version_id}`
        );

        if (response.status === 200) {
          setComplianceData(response.data?.data?.compliance || []);
        } else {
          console.error("Unexpected response:", response);
        }
      } catch (error) {
        console.error("Error fetching compliance data:", error);
        if (isFrameworkNotEnabledError(error)) setFrameworkDisabled(true);
      } finally {
        setIsTableLoading(false);
      }
    };
    const fetchAllComplianceData = async () => {
      try {
        const isCIS = lastSegment.toUpperCase() === "CIS";
        if (isCIS) {
          const filters = ["ig1", "ig2", "ig3"];
          const requests = filters.map((filter) =>
            axiosInstance.post("/securityscan/cd-compliance-posture", {
              control_type: TableData.Control_id,
              version: TableData.Version_id,
              filter,
            })
          );

          const responses = await Promise.all(requests);
          const data: any = responses
            .map((res) => res.data?.data?.compliance_data)
            .filter(Boolean);
          setComplianceIGArray(data);
        } else {
          const singleRes = await axiosInstance.post(
            "/securityscan/cd-compliance-posture",
            {
              control_type: TableData.Control_id,
              version: TableData.Version_id,
            }
          );
          const singleData = singleRes.data?.data?.compliance_data;
          if (singleData) {
            setComplianceIGArray([singleData]);
            setIsSummaryData({
              total_count: singleData.total_count,
              passed_count: singleData.passed_count,
              failed_count: singleData.failed_count,
              compliance: singleData.compliance,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching compliance posture data:", error);
        if (isFrameworkNotEnabledError(error)) setFrameworkDisabled(true);
      }
    };

    const fetchSummaryData = async () => {
      try {
        const payload = {
          control_type: TableData.Control_id,
          version: TableData.Version_id,
        };
        const response = await axiosInstance.post(
          "/securityscan/cd-compliance-posture",
          payload
        );
        const complianceData = response.data?.data?.compliance_data;

        if (complianceData) {
          setIsSummaryData({
            total_count: complianceData.total_count,
            passed_count: complianceData.passed_count,
            failed_count: complianceData.failed_count,
            compliance: complianceData.compliance,
          });
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
        if (isFrameworkNotEnabledError(error)) setFrameworkDisabled(true);
      }
    };

    if (TableData?.Control_id && TableData?.Version_id) {
      fetchData();
      fetchAllComplianceData();
      if (lastSegment.toUpperCase() === "CIS") {
        fetchSummaryData();
      }
    }
  }, [trigger, TableData?.Control_id, TableData?.Version_id]);



  const trendChartOptions = useMemo(
    () => ({
      chart: {
        type: "line" as const,
        height: 240,
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      colors: ["#6CBD54", "#F08B0E", "#02AFBD"], // Colors for IG1, IG2, IG3
      stroke: { curve: "smooth" as const, width: 3 },
      markers: {
        size: 4,
        hover: {
          sizeOffset: 2,
        },
      },
      xaxis: {
        categories: trendCategories,
        labels: {
          show: true,
          rotate: -30,
          style: {
            fontSize: "10px",
            colors: theme.palette.mode === "dark" ? "#fff" : "#000",
          },
          formatter: (val: string) => dayjs(val).format("DD/MMM/YY"),
          showDuplicates: false,
          trim: true,
          hideOverlappingLabels: true,
          maxHeight: 40,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        tooltip: {
          enabled: false, // Disable x-axis tooltip
        },
      },
      yaxis: {
        title: {
          text: "Compliance %",
          style: { color: theme.palette.text.secondary },
        },
        labels: {
          style: { colors: theme.palette.text.secondary },
          formatter: (val: number) => `${Math.round(val)}%`,
        },
        min: 0,
        max: 100,
      },
      legend: {
        show: false,
      },
      tooltip: {
        theme: theme.palette.mode,
        shared: true,
        intersect: false,
        x: {
          show: true,
          formatter: (val: any, opts: any) => {
            return dayjs(trendCategories[val]).format("DD/MMM/YY");
          },
        },
        y: {
          formatter: (val: number) => `${val.toFixed(2)}%`,
        },
      },
      grid: {
        borderColor: theme.palette.divider,
        padding: {
          left: 15,
          right: 40,
          top: 5,
          bottom: 45,
        },
      },
    }),
    [trendCategories, theme]
  );

  function handleNistRowClick(row: any) {
    setSelectedRow(row);
    setDrawerOpen(true);
  }
  function handleCisRowClick(row: any) {
    setSelectedRow(row);
    setDrawerOpen(true);
  }

  const isDarkMode = theme.palette.mode === "dark";

  if (frameworkDisabled) {
    return <FrameworkNotEnabled />;
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
      }}
    >
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={12}>
          <Accordion
            expanded={isSummaryExpanded}
            onChange={() => setIsSummaryExpanded(!isSummaryExpanded)}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "4px",
              bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "white",
              "&.Mui-expanded": {
                marginBottom: 0,
              },
              "&:before": {
                display: "none",
              },
            }}
            disableGutters
            elevation={0}
          >
            <AccordionSummary
              expandIcon={
                isSummaryExpanded ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowUpIcon />
                )
              }
              aria-controls="summary-content"
              id="summary-header"
              sx={{
                borderBottom: isSummaryExpanded
                  ? `1px solid ${theme.palette.divider}`
                  : "none",
                minHeight: "48px",
                bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
                "& .MuiAccordionSummary-root": {
                  flexDirection: "row-reverse",
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  transform: "none",
                  "&.Mui-expanded": {
                    transform: "none",
                  },
                },
                "& .MuiAccordionSummary-content": {
                  margin: "12px 0",
                },
                borderRadius: "8px 8px 0 0",
                fontWeight: 700,
                flexDirection: "row-reverse",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 3,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  SUMMARY
                </Typography>
                {!isSummaryExpanded && complianceIGArray.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      ml: "auto",
                    }}
                  >
                    {complianceIGArray.map((widgetData: any, index: number) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                              height: 24,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.2)"
                                  : "rgba(0,0,0,0.2)",
                            }}
                          />
                        )}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {widgetData.type || widgetData.control_type || `IG${index + 1}`}:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 150,
                            }}
                          >
                            <Box sx={{ flexGrow: 1, width: "100%" }}>
                              <LinearProgress
                                variant="determinate"
                                value={widgetData.compliance || 0}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? "#333"
                                      : "#f0f0f0",
                                  "& .MuiLinearProgress-bar": {
                                    bgcolor:
                                      (widgetData.compliance || 0) >= 75
                                        ? "green"
                                        : (widgetData.compliance || 0) >= 50
                                          ? "orange"
                                          : "red",
                                    borderRadius: 3,
                                  },
                                }}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, minWidth: 45 }}
                            >
                              {Math.round(widgetData.compliance || 0)}%
                            </Typography>
                          </Box>
                        </Box>
                      </React.Fragment>
                    ))}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />
                    {/* Total Controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Total Controls
                      </Typography>:
                      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                        {isSummaryData?.total_count}
                      </Typography>
                    </Box>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />
                    {/* Passed */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={checkMarkIcon}
                        alt="Passed "
                        style={{ width: 20, height: 20 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Passed:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#43a047" }}
                      >
                        {complianceIGArray.reduce(
                          (sum: number, data: any) =>
                            sum + (data.passed_count || 0),
                          0
                        )}
                      </Typography>
                    </Box>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />
                    {/* Failed */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={unCheckMarkIcon}
                        alt="Failed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Failed:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#e53935" }}
                      >
                        {complianceIGArray.reduce(
                          (sum: number, data: any) =>
                            sum + (data.failed_count || 0),
                          0
                        )}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 2,
                height: "40vh",
                overflow: "hidden",
                bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
              }}
            >
              {isLoading || !isSummaryExpanded ? (
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="stretch"
                  gap={2}
                  height="100%"
                >
                  <div
                    style={{
                      padding: 2,
                      flex: 0.3,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      sx={{
                        flex: 0.4,
                        minWidth: 120,
                        ml: -10,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          // mb: 2,
                          ml: 9,
                        }}
                      >
                        <Skeleton variant="circular" width={120} height={120} />
                        <Skeleton variant="circular" width={120} height={120} />
                        <Skeleton variant="circular" width={120} height={120} />

                        {/* <Skeleton variant="text" width={150} height={32} /> */}
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        flex: 0.6,
                        // ml: 2,
                        height: "100%",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ width: "fit-content" }}
                      >
                        {/* <Box display="flex" alignItems="center" mb={1}>
                          <Skeleton variant="text" width={120} height={24} />
                          <Skeleton variant="text" width={120} height={24} />
                          <Skeleton variant="text" width={120} height={24} />
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Skeleton variant="text" width={120} height={24} />
                          <Skeleton variant="text" width={120} height={24} />
                          <Skeleton variant="text" width={120} height={24} />
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Skeleton variant="text" width={120} height={24} />
                          <Skeleton variant="text" width={120} height={24} />
                          <Skeleton variant="text" width={120} height={24} />
                        </Box> */}
                      </Box>
                    </Box>
                  </div>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, my: 0 }}
                  />
                  <div
                    style={{
                      padding: 2,
                      flex: 0.7,
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Skeleton variant="text" width={100} height={28} />
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton variant="circular" width={10} height={10} />
                        <Skeleton variant="text" width={80} height={20} />
                        <Skeleton variant="text" width={40} height={20} />
                      </Box>
                    </Box>
                    <Skeleton variant="rectangular" width="100%" height={140} />
                  </div>
                </Box>
              ) : (
                <Grid container spacing={2} mt={0}>
                  <Grid item xs={12} md={5}>
                    {" "}
                    {/* Use a flex container for the widgets and dividers */}
                    <Box
                      display="flex"
                      alignItems="stretch"
                      justifyContent={
                        complianceIGArray.length === 1 ? "center" : "flex-start"
                      }
                      height="80%"
                    >
                      {complianceIGArray.map(
                        (widgetData: any, index: number) => (
                          <React.Fragment key={widgetData.control_type_id}>
                            <Box sx={{ flex: 1, minWidth: 0, p: 1, pt: 0 }}>
                              <ComplianceWidget data={widgetData} />
                            </Box>
                            {index < complianceIGArray.length - 1 && ( // Add divider after each widget except the last one
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ mx: 2, height: "100%" }}
                              /> // Added margin and ensured full height
                            )}
                          </React.Fragment>
                        )
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    {" "}
                    {/* Space for Trends chart */}
                    <Box height="60%" display="flex" flexDirection="column">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px",
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          TRENDS
                        </Typography>
                        {summaryPoints.length > 0 && (
                          <SummaryUI
                            summaryPoints={summaryPoints}
                            isDarkMode={isDarkMode}
                          />
                        )}
                        {/* <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <FiberManualRecordIcon sx={{ color: "#02AFBD" }} />
                            <Typography sx={{ fontWeight: "14px" }}>IG3</Typography>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "5px",
                                backgroundColor: "#00A61B1C",
                                borderRadius: "10px",
                                padding: "2px 6px",
                              }}
                            >
                              <ArrowOutwardIcon
                                sx={{ color: "#00A61B", fontSize: 16 }}
                              />
                              <Typography
                                sx={{ color: "#00A61B", fontWeight: "bold", fontSize: "14px" }}
                                variant="body2"
                              >
                                02%
                              </Typography>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <FiberManualRecordIcon sx={{ color: "#F08B0E" }} />
                            <Typography sx={{ fontWeight: "14px" }} >
                              IG2
                            </Typography>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "5px",
                                backgroundColor: "#F03C3C1C",
                                borderRadius: "10px",
                                padding: "2px 6px",
                              }}
                            >
                              <CallReceivedIcon
                                sx={{
                                  color: isDarkMode ? "#F03C3C" : "#F03C3C",
                                  fontSize: 16,
                                }}
                              />
                              <Typography sx={{ color: "#F03C3C", fontSize: "14px", fontWeight: "bold" }} variant="body2">
                                1.4%
                              </Typography>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <FiberManualRecordIcon sx={{ color: "#6AB852" }} />
                            <Typography sx={{ fontWeight: "14px" }}>IG1</Typography>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "5px",
                                backgroundColor: "#00A61B1C",
                                borderRadius: "10px",
                                padding: "2px 6px",
                              }}
                            >
                              <ArrowOutwardIcon
                                sx={{ color: "#00A61B", fontSize: 16 }}
                              />
                              <Typography
                                sx={{ color: "#00A61B", fontWeight: "bold", fontSize: "14px" }}
                                variant="body2"
                              >
                                04%
                              </Typography>
                            </div>
                          </div>
                        </div> */}
                      </div>
                      <ReactApexChart
                        options={trendChartOptions}
                        series={trendSeries}
                        type="line"
                        height={trendChartOptions.chart.height}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      <Paper
        sx={{
          p: 0,
          bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">Controls</Typography>
        </Box> */}
        <Box
          sx={{
            height: isSummaryExpanded
              ? "calc(52vh - 110px)"
              : "calc(91vh - 110px)",

            width: "100%",
          }}
        >
          <AgGridTable
            data={TableData_WidgetClicked}
            columnDefs={TableData_WidgetClicked_TABLE_COL_DEFS}
            pagination={true}
            styles={agGridInlineStyles}
            showSearch={true}
            searchPlaceholder="Search controls..."
            height={
              isSummaryExpanded ? "calc(46vh - 105px)" : "calc(86vh - 107.5px)"
            }
            isLoading={isTableLoading}
            onRowClick={handleCisRowClick}
          />
        </Box>
      </Paper>
      <ControlDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        row={selectedRow}
      />
    </Box>
  );
};

const ComplianceDetailsPage: React.FC = () => {
  const location = useLocation();
  const { isFrameworkOn } = useFrameworks();
  const searchParams = new URLSearchParams(location.search);
  const versionType = searchParams.get("type");
  const lastSegment = decodeURIComponent(
    location.pathname.split("/").filter(Boolean).pop() || ""
  );

  // Deep link (or stale state) to a framework switched off for the organization
  if (lastSegment && !isFrameworkOn(lastSegment)) {
    return <FrameworkNotEnabled />;
  }

  if (lastSegment && lastSegment.toUpperCase() !== "CIS") {
    return <NistDetailsDashboard version={versionType || undefined} />;
  }

  return <CisDetailsDashboard />;
};

function PCIDetailsDrawer({
  open,
  onClose,
  row,
}: {
  open: boolean;
  onClose: () => void;
  row: any;
}) {
  const [tab, setTab] = useState(0); // Always call hooks first
  const [isComplianceHedaer, setIsComplinaceHedaer] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  
  useEffect(() => {
    if (open) {
      setShowFullDescription(false);
    }
  }, [open]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  console.log(row, "PCIDSS row");
  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    if (!row) return;

    const fetchComplianceDetails = async () => {
      try {
        const response = await axiosInstance.get(
          `/securityscan/compliance-details?control_id=${row?.id}`
        );

        setIsComplinaceHedaer(response?.data?.data?.compliance);

        console.log("Success:", response?.data?.data?.compliance);
      } catch (error) {
        console.error("Error fetching compliance details:", error);
      }
    };

    fetchComplianceDetails();
  }, [trigger, open, row?.id]);

  if (!row) return null;

  const HeaderData = (
    isComplianceHedaer as {
      id: string;
      name: string;
      description: string;
      percentage: number;
      controls_count: number;
      asset_count: number;
      resource_count?: number;
      type: string[];
      stage: string;
    }[]
  )?.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description,
    percentage: item.percentage,
    controls_count: item.controls_count,
    asset_count: item.asset_count,
    resource_count: item.resource_count, // <-- add this line
    type: item.type,
    stage: item.stage,
  }));

  const getChipStyle = (type: string) => {
    switch (type) {
      case "Recover":
        return {
          backgroundColor: "#00A61B1A", // light green
          color: "#00A61B",
          border: "1px solid #00A61B",
        };
      case "Govern":
        return {
          backgroundColor: "#E5A8001A", // light orange
          color: "#E5A800",
          border: "1px solid #E5A800",
        };
      case "Respond":
        return {
          backgroundColor: "#F53B3B1A", // light teal
          color: "#F53B3B",
          border: "1px solid #F53B3B",
        };
      case "Detect":
        return {
          backgroundColor: "#F7941F1A", // light teal
          color: "#F7941F",
          border: "1px solid #F7941F",
        };
      case "Identify":
        return {
          backgroundColor: "#0485C21F", // light teal
          color: "#0485C2",
          border: "1px solid #0485C2",
        };
      case "Protect":
        return {
          backgroundColor: "#682EFA1F", // light teal
          color: "#682EFA",
          border: "1px solid #682EFA",
        };
      default:
        return {
          backgroundColor: "rgba(224, 224, 224, 0.3)",
          color: "black",
          border: "1px solid #e0e0e0",
        };
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "55%",
          // maxWidth: 700,
          p: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: 2,
          mt: 1,
        }}
      >
        <Box sx={{ position: "relative", width: "100%" }}>
          <Grid container alignItems="start">
            {/* ID and Name */}
            <Grid item xs>
              <Typography
                component="span"
                sx={{
                  color: "#1976d2",
                  fontWeight: 500,
                  fontSize: "14px",
                  backgroundColor: "#B6B6B626",
                }}
              >
                {row.controlId}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  ml: 1,
                  whiteSpace: "pre-line",
                  p: 0.5,
                }}
              >
                {HeaderData[0]?.name}
              </Typography>
            </Grid>
            {/* Cancel Icon fixed at right */}
            <Grid item>
              <Box sx={{ position: "relative", width: "40px", height: "40px" }}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    position: "absolute",

                    right: 0,
                    p: 0,
                    color: "#A6A5B6",
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#716DA0",
            fontWeight: 400,
            fontSize: "14px",
            mb: 1,
            ml: 2,
            whiteSpace: "pre-line",
            display: "-webkit-box",
            WebkitLineClamp: showFullDescription ? "unset" : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {HeaderData[0]?.description || "No description available"}
        </Typography>

        {HeaderData[0]?.description?.length > 225 && (
          <Typography
            variant="body2"
            color="primary"
            sx={{
              cursor: "pointer",
              mb: 2,
              ml: 2,
              color: isDarkMode ? "#FFFFFF" : "#3A2ADA",
            }}
            onClick={() => setShowFullDescription(!showFullDescription)}
          >
            {showFullDescription ? "Show less" : "Show more"}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={0.5} mb={3} mt={2}>
          <Box display="flex" alignItems="center">
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", ml: 2, mr: 1 }}
            >
              Compliance%:
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                whiteSpace: "nowrap",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: 30,
                  height: 30,
                }}
              >
                {/* Background circle */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={25}
                  thickness={7}
                  sx={{ color: "#c5c5d2" }}
                />
                {/* Foreground progress */}
                <CircularProgress
                  variant="determinate"
                  value={HeaderData[0]?.percentage || 0}
                  size={25}
                  thickness={7}
                  sx={{
                    color:
                      (HeaderData[0]?.percentage || 0) >= 75
                        ? "green"
                        : (HeaderData[0]?.percentage || 0) >= 50
                          ? "orange"
                          : "red",
                    position: "absolute",
                    left: 0,
                  }}
                />
              </Box>
              {/* <Typography variant="body2" fontWeight={600}>
                          {`${value}%`}
                        </Typography> */}
            </Box>

            <Typography sx={{ fontWeight: 900, fontSize: "16px" }}>
              {HeaderData[0]?.percentage ?? 0}%
            </Typography>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          />

          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
            Total Recommendations:{" "}
            <span style={{ fontWeight: 900, fontSize: "16px", marginLeft: 3 }}>
              {HeaderData[0]?.controls_count}
            </span>
          </Typography>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 1,
              borderColor: "#D2D2F6",
              height: 24,
              alignSelf: "center",
            }}
          />

          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
            Assets:{" "}
            <span style={{ fontWeight: 900, fontSize: "16px", marginLeft: 2 }}>
              {HeaderData[0]?.asset_count}
            </span>
          </Typography>

          {HeaderData &&
            HeaderData[0] !== undefined &&
            typeof HeaderData[0].resource_count === "number" &&
            HeaderData[0].resource_count > 0 && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    mx: 1,
                    borderColor: "#D2D2F6",
                    height: 24,
                    alignSelf: "center",
                  }}
                />
                <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                  Resources:{" "}
                  <span style={{ fontWeight: 600, fontSize: "16px" }}>
                    {HeaderData[0].resource_count}
                  </span>
                </Typography>
              </>
            )}
        </Box>
        <Divider />

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab
            label="Recommendations"
            sx={{ fontSize: "13px", fontWeight: 600 }}
          />
          <Tab label="Assets" sx={{ fontSize: "13px", fontWeight: 600 }} />
          {HeaderData[0]?.resource_count && (
            <Tab label="Resources" sx={{ fontSize: "13px", fontWeight: 600 }} />
          )}
        </Tabs>
        {tab === 0 && <PciBenchmarksTable rowid={row?.id} />}
        {tab === 1 && <PciAssetsTable rowid={row?.id} />}
        {tab === 2 && <PciResourcesTable rowid={row?.id} />}
      </Box>
    </Drawer>
  );
}

// Mock table components for PCI DSS
function PCIBenchmarksTable({ rowid }: { rowid: string }) {
  const mockData = Array.from({ length: 10 }, (_, index) => ({
    id: `${rowid}-${index}`,
    name: `PCI DSS Requirement ${index + 1}`,
    benchmarks: ["PCI DSS"],
    percentage: Math.floor(Math.random() * 100),
    recommendations: Math.floor(Math.random() * 10) + 1,
  }));

  const columnDefs = [
    {
      field: "name",
      headerName: "Requirement",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "percentage",
      headerName: "Compliance",
      width: 150,
      cellRenderer: (params: any) => {
        const value = params.value;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress
              variant="determinate"
              value={value}
              size={30}
              sx={{
                color: value >= 75 ? "green" : value >= 50 ? "orange" : "red",
              }}
            />
            <Typography>{value}%</Typography>
          </Box>
        );
      },
    },
    {
      field: "recommendations",
      headerName: "Controls",
      width: 120,
    },
  ];

  return (
    <AgGridTable
      columnDefs={columnDefs}
      data={mockData}
      height="calc(70vh - 110px)"
      showSearch
      searchPlaceholder="Search requirements..."
    />
  );
}

function PCIAssetsTable({ rowid }: { rowid: string }) {
  const mockData = Array.from({ length: 15 }, (_, index) => ({
    id: `${rowid}-asset-${index}`,
    name: `Asset ${index + 1}`,
    type: ["Server", "Database", "Network"][Math.floor(Math.random() * 3)],
    compliance: Math.floor(Math.random() * 100),
    controls: Math.floor(Math.random() * 20) + 5,
  }));

  const columnDefs = [
    {
      field: "name",
      headerName: "Asset Name",
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      width: 150,
    },
    {
      field: "compliance",
      headerName: "Compliance",
      width: 150,
      cellRenderer: (params: any) => {
        const value = params.value;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress
              variant="determinate"
              value={value}
              size={30}
              sx={{
                color: value >= 75 ? "green" : value >= 50 ? "orange" : "red",
              }}
            />
            <Typography>{value}%</Typography>
          </Box>
        );
      },
    },
    {
      field: "controls",
      headerName: "Controls",
      width: 120,
    },
  ];

  return (
    <AgGridTable
      columnDefs={columnDefs}
      data={mockData}
      height="calc(70vh - 110px)"
      showSearch
      searchPlaceholder="Search assets..."
    />
  );
}

const PCIDetailsDashboard: React.FC<{ version?: string }> = ({ version }) => {
  const theme = useTheme();
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [trendSeries, setTrendSeries] = useState<any[]>([]);
  const [trendCategories, setTrendCategories] = useState<any[]>([]);
  const TableData = useSelector((state: any) => state.ComplianceWidget);

  console.log("TableData", TableData);

  // Mock data for summary
  // const mockSummaryData = {
  //   total_count: 85,
  //   passed_count: 25,
  //   failed_count: 60,
  //   compliance: 45,
  // };

  // useEffect(() => {
  //   // Generate mock trend data
  //   const dates = Array.from({ length: 7 }, (_, i) => {
  //     return dayjs()
  //       .subtract(6 - i, "days")
  //       .format("YYYY-MM-DD");
  //   });

  //   setTrendCategories(dates);
  //   setTrendSeries([
  //     {
  //       name: "PCI DSS Compliance",
  //       data: [62, 64, 65, 63, 66, 67.3, 67.3].map((val) =>
  //         Number(val.toFixed(1))
  //       ),
  //     },
  //   ]);

  //   // Generate mock table data
  //   const mockTableData = controlRequirementMapping.map((control) => ({
  //     ...control,
  //     compliance: Math.floor(Math.random() * 100),
  //     recommendations: Math.floor(Math.random() * 200),
  //   }));

  //   setTableData(mockTableData);
  // }, []);

  // const columns: ColDef[] = [
  //   {
  //     field: "index",
  //     headerName: "#",
  //     maxWidth: 90,
  //     valueGetter: (params) => params.node?.rowIndex! + 1,
  //     cellStyle: { fontWeight: "bold" },
  //   },
  //   {
  //     field: "control_id",
  //     headerName: "Control ID",
  //     maxWidth: 130,
  //     cellStyle: { fontWeight: "bold" },
  //   },
  //   {
  //     field: "control_name",
  //     headerName: "Name",
  //     flex: 2,
  //     maxWidth: 1000,
  //     wrapText: false,
  //     cellRenderer: (params: any) => {
  //       return (
  //         <Tooltip title={params.value}>
  //           <Typography
  //             variant="body2"
  //             sx={{
  //               fontWeight: "bold",
  //               whiteSpace: "nowrap",
  //               overflow: "hidden",
  //               textOverflow: "ellipsis",
  //               width: "100%",
  //             }}
  //           >
  //             {params.value}
  //           </Typography>
  //         </Tooltip>
  //       );
  //     },
  //     cellStyle: {
  //       display: "flex",
  //       alignItems: "center",
  //     },
  //   },
  //   {
  //     field: "requirement_id",
  //     headerName: "Requirement",
  //     maxWidth: 180,
  //     cellRenderer: (params: any) => {
  //       const requirementText = `Requirement ${params.value}`;
  //       return (
  //         <Tooltip title={params.data.requirement_name}>
  //           <Chip
  //             label={requirementText}
  //             sx={{
  //               backgroundColor: "#E8F1FE",
  //               color: "#0F02C3",
  //               borderRadius: "4px",
  //               height: "24px",
  //               fontWeight: "bold",
  //               "&:hover": {
  //                 backgroundColor: "#E8F1FE",
  //               },
  //             }}
  //           />
  //         </Tooltip>
  //       );
  //     },
  //   },
  //   {
  //     field: "compliance",
  //     headerName: "Compliance%",
  //     maxWidth: 160,
  //     cellRenderer: (params: any) => {
  //       return (
  //         <Box
  //           sx={{
  //             display: "flex",
  //             alignItems: "center",
  //             height: "100%",
  //             justifyContent: "flex-start",
  //             paddingLeft: "8px",
  //           }}
  //         >
  //           <Box
  //             sx={{
  //               position: "relative",
  //               display: "inline-flex",
  //               marginRight: 1,
  //             }}
  //           >
  //             <CircularProgress
  //               variant="determinate"
  //               value={100}
  //               size={24}
  //               thickness={5}
  //               sx={{
  //                 color: "#E0E0E0",
  //                 position: "absolute",
  //                 "& .MuiCircularProgress-circle": {
  //                   strokeLinecap: "round",
  //                 },
  //               }}
  //             />
  //             <CircularProgress
  //               variant="determinate"
  //               value={params.value}
  //               size={24}
  //               thickness={5}
  //               sx={{
  //                 color: "#00A61B",
  //                 "& .MuiCircularProgress-circle": {
  //                   strokeLinecap: "round",
  //                 },
  //               }}
  //             />
  //           </Box>
  //           <Typography variant="body2" sx={{ fontWeight: "bold" }}>
  //             {params.value}%
  //           </Typography>
  //         </Box>
  //       );
  //     },
  //     cellStyle: {
  //       display: "flex",
  //       alignItems: "center",
  //       padding: 0,
  //     },
  //   },
  //   {
  //     field: "recommendations",
  //     headerName: "Recommendations",
  //     maxWidth: 180,
  //     cellStyle: { fontWeight: "bold" },
  //   },
  // ];

  useEffect(() => {
    // Scroll to the top of the page when the component is mounted
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchTrendData = async () => {
      setIsLoading(true);
      try {
        const today = dayjs().format("YYYY-MM-DD");
        const sevenDaysAgo = dayjs().subtract(7, "day").format("YYYY-MM-DD");
        const payload = {
          device_id: TableData?.Device_id || TableData?.device_id,
          duration: "custom_date_range",
          start_date: sevenDaysAgo,
          end_date: today,
          framework_data: version ? version.replace(/(PCI DSS)\s+/, "$1_") : "", // replace spaces with underscores, handle undefined
          is_compliance: true,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        const response = await axiosInstance.post(
          END_POINTS.DASHBOARD_COMPLIANCE,
          payload
        );
        if (response.status === 200 && response.data?.data?.graph_data) {
          // Transform the response to fit ApexCharts
          const graphData = response.data.data.graph_data;
          setTrendCategories(graphData.map((item: any) => item.date_time));
          // Dynamically get the field name from the version string - use the same format as the API response
          const fieldName = version ? version.replace(/(PCI DSS)\s+/, "$1_") : "";
          const seriesData = graphData.map((item: any) => {
            // Use the fieldName that matches the API response format
            if (typeof version === "string") {
              return item[fieldName] ?? 0;
            }
            return 0;
          });
          setTrendSeries([
            {
              name: version,
              data: seriesData,
            },
          ]);
          // Calculate percentage change from first to last value (like CIS)
          const firstValue = seriesData.length > 0 ? seriesData[0] : 0;
          const lastValue = seriesData.length > 0 ? seriesData[seriesData.length - 1] : 0;
          const diff = lastValue - firstValue;
          PCI_DSS_TREND_DATA.percentChange = `${Math.abs(diff).toFixed(1)}%`;
          PCI_DSS_TREND_DATA.isPositive = diff >= 0;
        }
      } catch (error) {
        console.error("Error fetching compliance trend data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (version) {
      fetchTrendData();
    }
  }, [version, TableData?.Device_id, TableData?.device_id]);

  const handlePCIRowClick = (params: any) => {
    setSelectedRow(params);
    setDrawerOpen(true);
  };

  const truncateToTwoDecimals = (num: number) => Math.floor(num * 100) / 100;

  const [complianceData, setComplianceData] = useState<ComplianceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);

  const TableData_isWidgetClicked = complianceData?.map(
    (item: any, index: number) => ({
      number: index + 1,
      id: item.id,
      controlId: item.control_id,
      name: item.name,
      description: item.description,
      compliance: item.percentage,
      recommendations: item.controls_count,
      stage: item.stage,
    })
  );
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      "& .ag-body-vertical-scroll": {
        width: "0px !important",
        minWidth: "0px !important",
      },
      "& .ag-body-vertical-scroll-viewport": {
        width: "0px !important",
        minWidth: "0px !important",
      },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  const TableData_WidgetClicked__isTABLE_COL_DEFS = [
    {
      headerName: "#",
      field: "number",
      minWidth: 80,
      maxWidth: 100,
      width: 90,
    },
    {
      headerName: "Control ID",
      field: "controlId",
      filter: true,
      sortable: true,
      minWidth: 130,
      maxWidth: 140,
      width: 120,

      cellStyle: {
        display: "flex",
        // justifyContent: "center",
        alignItems: "center",
      },
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip title={value}>
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "bold",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
    },

    {
      headerName: "Name",
      field: "name",
      filter: true,
      sortable: true,
      flex: 2,
      cellStyle: {
        display: "flex",
        // justifyContent: "center",
        alignItems: "center",
      },
      minWidth: 450,
      width: 450,

      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <WhiteTooltip title={value}>
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  width: "100%",
                  fontWeight: "bold",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
    },
    {
      headerName: "Description",
      field: "description",
      flex: 2,
      minWidth: 450,
      width: 450,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (

          <WhiteTooltip
            title={
              <div className="scroll" style={{ maxHeight: 200, overflowY: "auto", paddingRight: 8, }}>
                {value}
              </div>
            }
          >
            <span style={{ width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  fontWeight: "600",
                }}
              >
                {value}
              </Typography>
            </span>
          </WhiteTooltip>
        );
      },
      cellStyle: {
        display: "flex",
        // justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      headerName: "Compliance %",
      field: "compliance",
      sortable: true,
      minWidth: 150,
      maxWidth: 200,
      width: 180,

      cellRenderer: (params: any) => {
        const value = Number(params.value) || 0;

        // Choose progress color based on value
        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row", // explicitly set row direction
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: 40,
                height: 40,
              }}
            >
              {/* Background Circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{
                  color: "#c5c5d2", // background circle
                }}
              />
              {/* Foreground Progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
              {`${value}%`}
            </Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      headerName: "Recommendations",
      field: "recommendations",
      minWidth: 100,
      width: 100,
      maxWidth: 180,
      cellRenderer: (params: any) => {
        const value = params.value || "";
        // const shouldTruncate = value.length > 100;

        return (
          <span style={{ width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
                width: "100%",
                fontWeight: "500",
              }}
            >
              {value}
            </Typography>
          </span>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  const path = window.location.pathname;
  const parts = path.split("/");
  const lastPart = parts[parts.length - 1];
  console.log("framework", lastPart);

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  useEffect(() => {
    const handleFilterSaved = () => {
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);



  useEffect(() => {
    setIsTableLoading(true);
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get(
          `/securityscan/cd-compliance?control_type=${TableData.Control_id}&version=${TableData.Version_id}`
        );

        if (response.status === 200) {
          setComplianceData(response.data.data.compliance);
        } else {
          console.error("Unexpected response:", response);
        }
      } catch (error) {
        console.error("Error fetching compliance data:", error);
      } finally {
        setIsTableLoading(false);
      }
    };

    if (TableData.Control_id && TableData.Version_id) {
      fetchData();
    }
  }, [trigger, TableData.Control_id, TableData.Version_id]);

  const [isSummaryData, setIsSummaryData] = useState<{
    total_count: number;
    passed_count: number;
    failed_count: number;
    compliance: number;
  } | null>(null);
  //const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect


  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = {
          control_type: TableData.Control_id,
          version: TableData.Version_id,
        };
        const response = await axiosInstance.post(
          "/securityscan/cd-compliance-posture",
          payload
        );
        const complianceData = response.data?.data?.compliance_data;

        if (complianceData) {
          setIsSummaryData({
            total_count: complianceData.total_count,
            passed_count: complianceData.passed_count,
            failed_count: complianceData.failed_count,
            compliance: complianceData.compliance,
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [trigger, TableData.Control_id, TableData.Version_id]);

  const trendChartOptions = useMemo(
    () => ({
      chart: {
        type: "line" as const,
        height: 240,
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      colors: ["#6CBD54"],
      stroke: { curve: "smooth" as const, width: 3 },
      markers: {
        size: 4,
        hover: {
          sizeOffset: 2,
        },
      },
      xaxis: {
        categories: trendCategories,
        labels: {
          show: true,
          rotate: -30,
          style: {
            fontSize: "10px",
            colors: theme.palette.mode === "dark" ? "#fff" : "#000",
          },
          formatter: (val: string) => dayjs(val).format("DD/MMM/YY"),
          showDuplicates: false,
          trim: true,
          hideOverlappingLabels: true,
          maxHeight: 40,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        tickAmount: 8,
        tickPlacement: "on",
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        title: {
          text: "Compliance %",
          style: { color: theme.palette.text.secondary },
        },
        labels: {
          style: { colors: theme.palette.text.secondary },
          formatter: (val: number) => `${Math.round(val)}%`,
        },
        min: 0,
        max: 100,
      },
      legend: {
        position: "top" as const,
        horizontalAlign: "right" as const,
        labels: {
          colors: theme.palette.text.primary,
        },
      },
      tooltip: {
        theme: theme.palette.mode,
        shared: true,
        intersect: false,
        x: {
          show: true,
          formatter: (val: any, opts: any) => {
            return dayjs(trendCategories[val]).format("DD/MMM/YY");
          },
        },
        y: {
          formatter: (val: number) => `${val.toFixed(1)}%`,
        },
      },
      grid: {
        borderColor: theme.palette.divider,
        padding: {
          left: 15,
          right: 40,
          top: 5,
          bottom: 45,
        },
      },
    }),
    [trendCategories, theme]
  );

  return (
    <Box
      sx={{ bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit" }}
    >
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={12}>
          <Accordion
            expanded={isSummaryExpanded}
            onChange={() => setIsSummaryExpanded(!isSummaryExpanded)}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "4px",
              bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
              "&.Mui-expanded": {
                marginBottom: 0,
              },
              "&:before": {
                display: "none",
              },
            }}
            disableGutters
            elevation={0}
          >
            <AccordionSummary
              expandIcon={
                isSummaryExpanded ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowUpIcon />
                )
              }
              aria-controls="summary-content"
              id="summary-header"
              sx={{
                borderBottom: isSummaryExpanded
                  ? `1px solid ${theme.palette.divider}`
                  : "none",
                minHeight: "48px",
                flexDirection: "row-reverse",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(0deg)",
                },
                borderRadius: "8px 8px 0 0",
                fontWeight: 700,
                bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 3,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  SUMMARY
                </Typography>
                {!isSummaryExpanded && isSummaryData && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      ml: "auto",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Compliance:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 150,
                      }}
                    >
                      <Box sx={{ flexGrow: 1, width: "100%" }}>
                        <LinearProgress
                          variant="determinate"
                          value={Number(
                            (isSummaryData.compliance || 0).toFixed(2)
                          )}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            mr: 2,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "#333"
                                : "#f0f0f0",
                            "& .MuiLinearProgress-bar": {
                              bgcolor:
                                (isSummaryData.compliance || 0) >= 75
                                  ? "green"
                                  : (isSummaryData.compliance || 0) >= 50
                                    ? "orange"
                                    : "red",
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, minWidth: 45 }}
                      >
                        {Number((isSummaryData?.compliance || 0).toFixed(2))}%
                      </Typography>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />

                    <Typography sx={{ fontWeight: 500, fontSize: "12px" }}>
                      Total Controls:{" "}
                      <span style={{ fontWeight: 800, fontSize: 16 }}>
                        {isSummaryData.total_count}
                      </span>
                    </Typography>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={checkMarkIcon}
                        alt="Passed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Passed:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#43a047" }}
                      >
                        {isSummaryData.passed_count}
                      </Typography>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: 24,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.2)",
                      }}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={unCheckMarkIcon}
                        alt="Failed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Failed:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#e53935" }}
                      >
                        {isSummaryData.failed_count}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 3,
                borderRadius: "0 0 8px 8px",
                height: "30vh",
                overflow: "hidden",
                background:
                  theme.palette.mode === "dark" ? "#1E1E1E" : "inherit",
              }}
            >
              {isLoading || !isSummaryData ? (
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="stretch"
                  gap={2}
                  height="100%"
                >
                  <div
                    style={{
                      padding: 2,
                      flex: 0.3,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      sx={{
                        flex: 0.4,
                        minWidth: 120,
                        ml: -10,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                          ml: 9,
                        }}
                      >
                        <Skeleton variant="circular" width={140} height={140} />
                        {/* <Skeleton variant="text" width={150} height={32} /> */}
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        flex: 0.6,
                        ml: 2,
                        height: "100%",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ width: "fit-content" }}
                      >
                        <Box display="flex" alignItems="center" mb={1}>
                          <Skeleton variant="text" width={120} height={24} />
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Skeleton
                            variant="rectangular"
                            width={100}
                            height={24}
                          />
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Skeleton
                            variant="rectangular"
                            width={100}
                            height={24}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, my: 0 }}
                  />
                  <div
                    style={{
                      padding: 2,
                      flex: 0.7,
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Skeleton variant="text" width={100} height={28} />
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton variant="circular" width={10} height={10} />
                        <Skeleton variant="text" width={80} height={20} />
                        <Skeleton variant="text" width={40} height={20} />
                      </Box>
                    </Box>
                    <Skeleton variant="rectangular" width="100%" height={140} />
                  </div>
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="stretch"
                  gap={2}
                  height="100%"
                >
                  <div
                    style={{
                      padding: 2,
                      flex: 0.3,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      sx={{
                        flex: 0.4,
                        minWidth: 120,
                        ml: -10,
                      }}
                    >
                      <ReactApexChart
                        key={theme.palette.mode}
                        options={{
                          chart: {
                            type: "radialBar",
                            sparkline: { enabled: true },
                          },
                          plotOptions: {
                            radialBar: {
                              hollow: { size: "60%" },
                              dataLabels: {
                                name: {
                                  show: true,
                                  fontSize: "16px",
                                  fontWeight: 600,
                                  //color: "#8884d8",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#8884d8",
                                  offsetY: 24,
                                },
                                value: {
                                  fontSize: "22px",
                                  fontWeight: 600,
                                  offsetY: -10,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#696595",
                                },
                              },
                            },
                          },
                          fill: { colors: ["#6c7ba1"] },
                          stroke: { lineCap: "round" },
                          labels: ["Compliance%"],
                        }}
                        series={[
                          truncateToTwoDecimals(isSummaryData?.compliance || 0),
                        ]}
                        type="radialBar"
                        height={185}
                      />
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        flex: 0.6,
                        ml: 2,
                        height: "100%",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ width: "fit-content" }}
                      >
                        <Box display="flex" alignItems="center" mb={1}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 16,
                              mr: 1,
                            }}
                          >
                            Total Controls  :
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            {isSummaryData?.total_count}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <img
                            src={checkMarkIcon}
                            alt="Passed"
                            style={{ width: 20, height: 20, marginRight: 10 }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 16,
                              mr: 3,
                            }}
                          >
                            Passed :
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#43a047",
                              fontSize: 16,
                            }}
                          >
                            {isSummaryData?.passed_count}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <img
                            src={unCheckMarkIcon}
                            alt="Failed"
                            style={{ width: 20, height: 20, marginRight: 10 }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 16,
                              mr: 3,
                            }}
                          >
                            Failed :
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#e53935",
                              fontSize: 16,
                            }}
                          >
                            {isSummaryData?.failed_count}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </div>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, my: 0 }}
                  />
                  <div
                    style={{
                      padding: 2,
                      flex: 0.7,
                      height: "100%",
                      boxShadow: "none",
                      background: "transparent",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16 }}
                      >
                        TRENDS
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "#3B82F6",
                          }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ fontSize: 14 }}
                        >
                          PCI DSS Compliance
                        </Typography>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap="4px"
                          sx={{
                            backgroundColor: PCI_DSS_TREND_DATA.isPositive ? "#00A61B1C" : "#F03C3C1C",
                            borderRadius: "10px",
                            padding: "2px 6px",
                          }}
                        >
                          {PCI_DSS_TREND_DATA.isPositive ? (
                            <ArrowOutwardIcon sx={{ color: "#00A61B", fontSize: 16 }} />
                          ) : (
                            <CallReceivedIcon sx={{ color: "#F03C3C", fontSize: 16 }} />
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontSize: 14,
                              color: PCI_DSS_TREND_DATA.isPositive ? "#00A61B" : "#F03C3C"
                            }}
                          >
                            {PCI_DSS_TREND_DATA.percentChange}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <ReactApexChart
                      options={{
                        ...trendChartOptions,
                        chart: {
                          ...trendChartOptions.chart,
                          height: 140,
                        },
                        xaxis: {
                          ...trendChartOptions.xaxis,
                          categories: trendCategories,
                          labels: {
                            rotate: -30,
                            style: {
                              fontSize: "10px",
                              colors:
                                theme.palette.mode === "dark" ? "#fff" : "#000",
                            },
                            formatter: (val: string) =>
                              dayjs(val).format("DD/MMM/YY"),
                            showDuplicates: false,
                            trim: true,
                            hideOverlappingLabels: true,
                            maxHeight: 40,
                          },
                          tickAmount: 8,
                          tickPlacement: "on",
                          tooltip: {
                            enabled: false, // Disable x-axis tooltip
                          },
                        },
                        yaxis: {
                          ...trendChartOptions.yaxis,
                          labels: {
                            formatter: (val: number) => `${Math.round(val)}%`,
                            style: {
                              fontSize: "10px",
                              colors:
                                theme.palette.mode === "dark" ? "#fff" : "#000",
                            },
                          },
                          min: 0,
                          max: 100,
                          tickAmount: 4,
                        },
                        tooltip: {
                          theme: theme.palette.mode,
                          shared: true,
                          intersect: false,
                          x: {
                            show: true,
                            formatter: (val: any, opts: any) => {
                              return dayjs(trendCategories[val]).format(
                                "DD/MMM/YY"
                              );
                            },
                          },
                          y: {
                            formatter: (val: number) => `${val.toFixed(2)}%`,
                          },
                        },
                        grid: {
                          ...trendChartOptions.grid,
                          padding: {
                            left: 15,
                            right: 40,
                            top: 5,
                            bottom: 5,
                          },
                        },
                      }}
                      series={trendSeries}
                      type="line"
                      height={140}
                    />
                  </div>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      <Paper
        sx={{
          pb: 0.5,
          background: theme.palette.mode === "dark" ? "#1E1E1E" : "#fff",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            height: isSummaryExpanded
              ? "calc(61vh - 110px)"
              : "calc(91vh - 110px)",
            width: "100%",
          }}
        >
          <AgGridTable
            data={TableData_isWidgetClicked}
            columnDefs={TableData_WidgetClicked__isTABLE_COL_DEFS}
            isLoading={isTableLoading}
            pagination={true}
            styles={agGridInlineStyles}
            showSearch={true}
            searchPlaceholder="Search controls..."
            height={
              isSummaryExpanded ? "calc(55vh - 110px)" : "calc(85vh - 110px)"
            }
            onRowClick={handlePCIRowClick}
          />
        </Box>
      </Paper>

      <PCIDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        row={selectedRow}
      />
    </Box>
  );
};

export default ComplianceDetailsPage;
